import { useEffect, useMemo, useRef, useState } from "react";
import { setRenderDebugEnabled, timeDebugStep, wrapRafCallback } from "@/renderer/renderDebug";
import { createPointerRouter } from "@/editor/canvas/pointerRouter";
import { CanvasRenderController } from "@/editor/canvas/CanvasRenderController";
import {
  activeGradientForSelection,
  activeImageFillForSelection,
  buildRenderOverlay,
  vectorSmartTransformOverlay,
  type RenderOverlayOverrides,
} from "@/editor/canvas/renderOverlay";
import { documentStore, editorScriptsStore, selectionStore, uiStore, viewStore } from "@/editor/store";
import { findScriptEntry } from "@/document/model/documentScripts";
import {
  disableWasmMeshKernels,
  enableWasmMeshKernels,
} from "@/renderer/scene/tessellation/curvaTessWasm";
import { getGeometryPool } from "@/renderer/scene/tessellation/geometryPool";
import { setSubdivideConnect } from "@/editor/tools/subdivideSession";
import { useStoreSubscribe } from "@/lib/store";
import { DEFAULT_DOCUMENT_VIEWPORT, findSceneNode, getActivePageSceneNodes, getAllSceneNodes, resolveActivePageId, type CurvaDocument, type DocumentViewport, type PrototypeLink } from "@/document/model/document";
import { prototypeLinks } from "@/document/model/prototype";
import { createInstanceFromComponent } from "@/document/operations/componentSystem";
import type { VectorNetwork } from "@/document/model/vectorNetwork";
import { createAnimationGraphNode, createEmptyAnimationGraph, normalizeAnimationGraph, type AnimationNodeKind } from "@/document/model/animation";
import { renderAnimationPreviewToCanvas } from "./animationPreviewRender";
import { renderScriptPreviewToCanvas } from "./scriptPreviewRender";
import { materializeScriptPreviewDocument } from "@/commands/scriptCommands";
import { animationSceneNodeDropAction } from "./animationSceneNodeDrop";
import { sampleEdgeBezier } from "@/document/operations/vectorGeometry";
import { snapPointToVertex } from "@/document/operations/vectorVertexSnap";
import { vectorHandleUpdatesForMove, type VectorHandleUpdate } from "@/document/operations/vectorHandleMirroring";
import { hitTestVectorRegion } from "@/document/operations/vectorRegions";
import { hitTestShapeBuilderRegion, shapeBuilderRegions, type ShapeBuilderSelection } from "@/document/operations/shapeBuilder";
import { type FrameNode, type SceneNode, type VectorNetworkNode } from "@/document/model/nodes";
import type { PrimitiveKind } from "@/document/model/primitives";
import { editableNodeIds, isNodeLockedForEditing } from "@/document/operations/nodeLocking";
import { nodeSelectionDragDeltaToLocal, nodeSelectionPointToLocal, nodeSelectionPointToWorld } from "@/document/operations/nodeWorldTransform";
import {
  getNodesInMarquee,
  getNodesInLasso,
  getSelectionBounds,
  hitTestResizeHandle,
  hitTestSelectionRotationHandle,
  hitTestDirectChildOfContainer,
  hitTestDocument,
  normalizeRect,
  offsetBounds,
  resizeBounds,
  transformBoundsInGroup,
  type RotationHandle,
  type ResizeHandle,
} from "@/editor/tools/hitTest";
import { visibleObjectSnapCandidates, type ObjectSnapGuide } from "@/editor/tools/objectSnap";
import { hitTestGuide, pointerInHorizontalRuler, pointerInVerticalRuler } from "@/editor/tools/guideHitTest";
import { collectResolvedGuides, guideScopesEqual } from "@/document/model/guides";
import type { GuideSelection } from "@/document/model/guides";
import { computeMeasureOverlay, computeVectorMeasureOverlay, isMeasureModifierActive } from "@/editor/tools/measureOverlay";
import {
  createPencilTraceSession,
  imageFillForPencilTrace,
  snapPointToPencilTrace,
  type PencilTraceSession,
} from "@/editor/tools/pencilTrace";
import { snapVectorPointToGeometry, vectorEditGuidesToWorld } from "@/editor/tools/vectorGeometrySnap";
import {
  bentEdgeHandles,
  hitTestVectorEdge,
  hitTestVectorHandle,
  hitTestVectorIntersection,
  hitTestVectorVertex,
  insertionForEdge,
  previewPointForEdge,
  type VectorEdgeHit,
  type VectorIntersectionHit,
} from "@/editor/tools/vectorEditHitTest";
import {
  hitTestGradientGizmo,
  paintWithGradientEndpoint,
  paintWithGradientStopPosition,
  paintWithRadialCenter,
  paintWithRadialRadiusPoint,
  paintWithRadialStopPosition,
  stopPositionForPoint,
  type GradientGizmoHandle,
} from "@/editor/tools/gradientGizmo";
import {
  hitTestImageFillGizmo,
  imagePaintWithMovedCrop,
  imagePaintWithResizedCrop,
  type ImageFillHandle,
} from "@/editor/tools/imageFillGizmo";
import {
  hitTestPrimitiveGizmo,
  ovalWithArcEndDrag,
  ovalWithArcStartDrag,
  ovalWithRatioDrag,
  polygonWithCountDrag,
  polygonWithCurvatureDrag,
  starWithCountDrag,
  starWithRatioDrag,
} from "@/editor/tools/primitiveGizmo";
import { isEditableShortcutTarget } from "@/editor/actions/keymap";
import { screenToDocument, clampZoom } from "@/editor/state/viewportStore";
import type { Rect } from "@/lib/math/rect";
import { inverseRotatePoint, normalizeDegrees, rectCenter, rotatePoint, transformRotationOrigin } from "@/lib/math/rotation";
import { createDefaultCamera, type Camera } from "@/renderer/camera";
import { Renderer } from "@/renderer/Renderer";
import type {
  GuidePreviewOverlay,
  KnifePreviewOverlay,
  RenderOverlay,
  RenderPreview,
  SceneOverlay,
  SelectionOverlay,
  ShapeBuilderRegionOverlay,
} from "@/renderer/overlayTypes";
import { overlayRequiresFullScene } from "@/renderer/sceneComposition";
import { settledZoomBucketForScale } from "@/renderer/tiles/zoomBuckets";
import {
  clearPointerHoverState,
  coarseHitTestGeometry,
  createPointerHoverState,
  patchPointerHoverState,
  viewportBoundsForHitTest,
  type PointerHoverState,
} from "./pointerHover";
import { textIndexAtPoint } from "@/renderer/text/canvasKitText";
import {
  appendPencilPointsToSession,
  createPencilVectorizeSession,
  pencilVectorizeSessionToNetwork,
  type PencilVectorizeSession,
} from "@/editor/tools/pencilVectorize";
import { sampleRenderedCanvas, type ColorSamplePreview } from "./colorSampler";
import { ColorSampleLoupe, FrameNameInput } from "./CanvasWorkspaceOverlays";
import { closePanelFloatingUi } from "./right-panel/PanelSelect";
import {
  applySelectionClick,
  applySelectionPointerDown,
  animationFrameDragModeForPoint,
  cameraFromDocumentViewport,
  cameraToDocumentViewport,
  canvasViewportBounds,
  constrainResizeBoundsAspectRatioAroundCenter,
  constrainResizeBoundsAspectRatio,
  deepSelectModifier,
  dragOffsetFromPreview,
  findDeepestFrameAtPoint,
  findTopFrameAtPoint,
  flexReorderPreviewForDrag,
  hitTestRotationOrigin,
  hitTestVectorSmartTransform,
  isDoubleClickContainer,
  isDrawableBounds,
  mergeSelection,
  movePreviewsForDrag,
  normalizeWheelDelta,
  pointInBounds,
  previewSelectionBounds,
  resizeBoundsAroundCenter,
  rotationOriginOverlay,
  rotationOriginSnapPoints,
  selectionBoundsForTransforms,
  selectionRotationForNodes,
  selectionRotationOriginForNodes,
  shapeDrawBounds,
  smartTransformSelectionKey,
  snapBoundsOriginToPixelGrid,
  snapShapeDrawBoundsToFrame,
  snapDegrees,
  snapPointToAngle,
  snapPointToPixelGrid,
  snapToPixelGridValue,
  snapRotationOrigin,
  toggleEdgeSelection,
  toggleVertexSelection,
  transformPointBetweenRotatedBounds,
  vectorEditLocalPoint,
} from "./canvasGeometry";
import {
  commandArrowSelection,
  hasTextRange,
  isArrowKey,
  isModKey,
  normalizedTextRange,
  selectionFromTextarea,
  setTextareaSelection,
  textWithDeletedSelection,
  wordBoundariesAtIndex,
} from "./canvasTextEditing";
import { frameLabelOverlays, frameLabelRenderItems, hitTestFrameLabel, hitTestFrameLabelScriptAction } from "./frameLabels";
import {
  buildDocumentGuideOverlay,
  guideDocumentPositionFromPointer,
  guidePositionForScope,
  guideSelectionFromResolved,
  pointInsideCanvasContentArea,
  resolveGuidePlacement,
  resolveGuideScopeAtPoint,
} from "./guideInteraction";
import { RulerOverlay } from "./RulerOverlay";
import { AnimationGraphOverlay, animationNodesInRect, nearestAnimationObjectInputPin, type AnimationGraphOverlayHandle } from "./AnimationGraphOverlay";
import { ANIMATION_PANEL_CONTROLS_HEIGHT, ANIMATION_TIMELINE_HEIGHT } from "./AnimationPanel";
import { createPencilVectorNetworkNode } from "./shellVectorConversion";
import { topFrameIdAtPoint } from "./shellSelection";
import type { ActiveTool } from "./TopBar";
import type { CanvasWorkspaceProps, DragState, FrameLabelOverlay } from "./canvasWorkspaceTypes";
import { CURVA_SCENE_NODE_DRAG_TYPE, CURVA_SCRIPT_ENTRY_DRAG_TYPE, getActiveSceneNodeDrag, getActiveScriptEntryDrag, isImageFileDrag, parseSceneNodeDrag, setActiveSceneNodeDrag } from "./shellTransfer";

const STATUS_BAR_HEIGHT = 28;
const ROTATION_CURSOR = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24'%3E%3Cpath d='M17.6 8.4A6.5 6.5 0 1 0 18 16' fill='none' stroke='%230f172a' stroke-width='4.4' stroke-linecap='round' opacity='.75'/%3E%3Cpath d='M18 4v5h-5' fill='none' stroke='%230f172a' stroke-width='4.4' stroke-linecap='round' stroke-linejoin='round' opacity='.75'/%3E%3Cpath d='M17.6 8.4A6.5 6.5 0 1 0 18 16' fill='none' stroke='%23f8fafc' stroke-width='2.4' stroke-linecap='round'/%3E%3Cpath d='M18 4v5h-5' fill='none' stroke='%23f8fafc' stroke-width='2.4' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E") 12 12, alias`;
const WHEEL_PAN_SPEED = 2.5;
const WHEEL_ZOOM_SPEED = 1.5;
const COMPONENT_DRAG_THRESHOLD = 4;
const MOVE_AUTOPAN_EDGE_ZONE_PX = 10;
const MOVE_AUTOPAN_MAX_STEP_PX = 18;

function dragAutopanDelta(
  pointer: { clientX: number; clientY: number },
  rect: DOMRect,
  insets: { top: number; right: number; bottom: number; left: number },
): { x: number; y: number } {
  const visible = {
    left: rect.left + insets.left,
    top: rect.top + insets.top,
    right: rect.right - insets.right,
    bottom: rect.bottom - insets.bottom,
  };
  return {
    x: dragAutopanAxis(pointer.clientX, visible.left, visible.right),
    y: dragAutopanAxis(pointer.clientY, visible.top, visible.bottom),
  };
}

function dragAutopanAxis(value: number, min: number, max: number): number {
  const band = Math.max(1, Math.min(MOVE_AUTOPAN_EDGE_ZONE_PX, (max - min) / 2));
  if (value < min + band) {
    return -MOVE_AUTOPAN_MAX_STEP_PX * Math.min(1, (min + band - value) / band);
  }
  if (value > max - band) {
    return MOVE_AUTOPAN_MAX_STEP_PX * Math.min(1, (value - (max - band)) / band);
  }
  return 0;
}

function objectSnapGuidesEqual(left: ObjectSnapGuide[], right: ObjectSnapGuide[]): boolean {
  return left.length === right.length && left.every((guide, index) => {
    const other = right[index];
    return other !== undefined
      && guide.orientation === other.orientation
      && guide.position === other.position
      && guide.from === other.from
      && guide.to === other.to
      && guide.x1 === other.x1
      && guide.y1 === other.y1
      && guide.x2 === other.x2
      && guide.y2 === other.y2;
  });
}

export function CanvasWorkspace({
  onViewportChange,
  onZoomChange,
  onPointerStatusChange,
  onToolChange,
  documentActions,
  vectorActions,
  animationActions,
}: CanvasWorkspaceProps) {
  const {
    onSelectionChange,
    onUndo,
    onRedo,
    onShapeDraw,
    onFrameDraw,
    onBoundsCommit,
    onFlexReorderCommit,
    onBoundsPreviewChange,
    onTransformCommit,
    onRotationOriginCommit,
    onDuplicateDrag,
    onGradientPaintChange,
    onPrimitiveChange,
    onImageFileDrop,
    onScriptEntryDrop,
    onContextMenu,
    onColorSample,
    onComponentPlacementEnd,
    onComponentPlacementCommit,
    onGuideSelectionChange,
    onGuideAdd,
    onGuideMove,
    onGuideRelocate,
    onGuideDelete,
    onPrototypeLinkCreate,
    onPrototypeLinkSelect,
    onRenameNode,
    onTextInsert,
    onTextEditExit,
    onTextCharactersChange,
    onTextBoldShortcut,
    onTextSelectionChange,
    onEnterTextMode,
  } = documentActions;
  const {
    onEnterVectorMode,
    onExitVectorMode,
    onSelectedVertexChange,
    onSelectedVerticesChange,
    onSelectedEdgesChange,
    onVertexCommit,
    onEdgeMoveCommit,
    onSelectedHandleChange,
    onHandleCommit,
    onEdgeBendCommit,
    onKnifeCutEdges,
    onKnifeCutAroundVertex,
    onVectorRegionFillToggle,
    onShapeBuilderCommit,
    onPathInsertVertex,
    onPathInsertIntersection,
    onPathToolStart,
    onPenAddVertex,
    onPenConnectVertices,
    onPenConnectToInsertedVertex,
    onPencilDraw,
    onDuplicateVerticesDragStart,
  } = vectorActions;
  const {
    onAnimationNodeInsert,
    onAnimationNodeArm,
    onAnimationNodeSelectionChange,
    onAnimationConnectionSelectionChange,
    onAnimationObjectConnectionSelectionChange,
    onAnimationNodesMove,
    onAnimationNodesDuplicate,
    onAnimationConnect,
    onAnimationObjectConnect,
    onAnimationTargetsConnect,
    onAnimationObjectCreateNode,
    onAnimationObjectsCreateGroupNode,
    onAnimationInsertAndConnect,
    onAnimationNodeUpdate,
    onAnimationVisualGroupUpdate,
    onAnimationVisualGroupDelete,
    onAnimationAddAnimateProperty,
    onAnimationPlay,
    onAnimationPause,
  } = animationActions;

  const document = useStoreSubscribe(documentStore.document);
  const selection = useStoreSubscribe(selectionStore.selection);
  const tool = useStoreSubscribe(selectionStore.tool);
  const freshCutVertexIds = useStoreSubscribe(selectionStore.freshCutVertexIds);
  const selectedObjectConnectionKeys = useStoreSubscribe(selectionStore.selectedObjectConnectionKeys);
  const animation = useStoreSubscribe(uiStore.animation);
  const canvasDebug = useStoreSubscribe(uiStore.canvasDebug);
  const canvasSnap = useStoreSubscribe(uiStore.canvasSnap);
  const sculptBrush = useStoreSubscribe(uiStore.sculptBrush);
  const colorSampler = useStoreSubscribe(uiStore.colorSampler);
  const rightPanelTab = useStoreSubscribe(uiStore.rightPanelTab);
  const uiChromeMode = useStoreSubscribe(uiStore.uiChromeMode);
  const selectedPrototypeLinkId = useStoreSubscribe(uiStore.selectedPrototypeLinkId);
  const editingImageFillTarget = useStoreSubscribe(uiStore.editingImageFillTarget);
  const editingGradientFillTarget = useStoreSubscribe(uiStore.editingGradientFillTarget);
  const selectedGuide = useStoreSubscribe(uiStore.selectedGuide);
  const allowScripts = useStoreSubscribe(uiStore.allowScripts);
  const scriptParamPreview = useStoreSubscribe(uiStore.scriptParamPreview);
  const textPreviews = useStoreSubscribe(viewStore.textPreviews);
  const fontsReadyToken = useStoreSubscribe(viewStore.fontsReadyToken);
  const resetZoomToken = useStoreSubscribe(viewStore.resetZoomToken);
  const viewportTarget = useStoreSubscribe(viewStore.viewportTarget);
  const componentPlacement = useStoreSubscribe(viewStore.componentPlacement);
  // Camera stays in a ref during pan/zoom — publishing every wheel tick through
  // useStoreSubscribe re-rendered this whole workspace (and rebuilt the pointer
  // router) on Stresstest-scale docs whenever something was selected.
  const previews = useStoreSubscribe(viewStore.previews);
  const vertexPreview = useStoreSubscribe(viewStore.vertexPreview);
  const handlePreview = useStoreSubscribe(viewStore.handlePreview);
  const edgeBendPreview = useStoreSubscribe(viewStore.edgeBendPreview);

  const resolvedActivePageId = useMemo(
    () => resolveActivePageId(document, selection.activePageId),
    [document, selection.activePageId],
  );
  const selectedNodeIds = selection.selectedNodeIds;
  const primarySelectedNodeId = selection.primarySelectedNodeId;
  const mode = selection.mode;
  const selectedVertexId = selection.selectedVertexId;
  const selectedVertexIds = selection.selectedVertexIds ?? [];
  const selectedEdgeIds = selection.selectedEdgeIds ?? [];
  const selectedHandle = selection.selectedHandle;
  const selectedAnimationNodeIds = selection.selectedAnimationNodeIds ?? [];
  const selectedAnimationConnectionIds = selection.selectedAnimationConnectionIds ?? [];
  const activeTool = tool.activeTool;
  const selectedShapeKind = tool.selectedShapeKind;
  const animationPanelOpen = animation.panelOpen;
  const animationTimelineOpen = animation.timelineOpen;
  const animationPlaying = animation.playing;
  const activeAnimationRootId = animation.activeRootId;
  const animationTimeMs = animation.timeMs;
  const armedAnimationNodeKind = animation.armedNodeKind;
  const vectorTargetNodeId = animation.vectorTargetNodeId;
  const showPixelGrid = canvasDebug.showPixelGrid;
  const showRenderDebug = canvasDebug.showRenderDebug;
  const renderTriangles = canvasDebug.renderTriangles;
  const showTileGaps = canvasDebug.showTileGaps;
  const useWasmTessellation = canvasDebug.useWasmTessellation;
  const showRulers = canvasDebug.showRulers;
  const snapToPixelGrid = canvasSnap.snapToPixelGrid;
  const snapToObjects = canvasSnap.snapToObjects;
  const snapToGuides = canvasSnap.snapToGuides;
  const snapToGeometry = canvasSnap.snapToGeometry;
  const assistedPencilTracing = canvasSnap.assistedPencilTracing;
  const prototypeModeActive = rightPanelTab === "prototype";
  const devModeActive = rightPanelTab === "dev" || animationPanelOpen;
  const animationModeActive = animationPanelOpen;
  const animationGraph = useMemo(
    () => normalizeAnimationGraph(document.animation ?? createEmptyAnimationGraph()),
    [document.animation],
  );
  const prototypeLinksList = useMemo(() => prototypeLinks(document), [document]);
  const animationFrames = useMemo(
    () => getActivePageSceneNodes(document, resolvedActivePageId).filter((node): node is FrameNode => node.type === "frame"),
    [document, resolvedActivePageId],
  );
  const visibleViewportInsets = useMemo(() => {
    const bottomChromeInset = STATUS_BAR_HEIGHT + (animationPanelOpen
      ? ANIMATION_PANEL_CONTROLS_HEIGHT + (animationTimelineOpen ? ANIMATION_TIMELINE_HEIGHT : 0)
      : 0);
    return uiChromeMode === "visible"
      ? { top: 48, left: 256, right: 288, bottom: bottomChromeInset }
      : uiChromeMode === "toolsSidebar"
        ? { top: 0, left: 256, right: 0, bottom: 0 }
        : { top: 0, left: 0, right: 0, bottom: 0 };
  }, [uiChromeMode, animationPanelOpen, animationTimelineOpen]);
  const [objectSnapGuides, setObjectSnapGuidesState] = useState<ObjectSnapGuide[]>([]);
  const setObjectSnapGuides = (guides: ObjectSnapGuide[]): void => {
    setObjectSnapGuidesState(current => objectSnapGuidesEqual(current, guides) ? current : guides);
  };
  const [guidePreview, setGuidePreview] = useState<GuidePreviewOverlay | undefined>(undefined);
  const [pointerCanvasOffset, setPointerCanvasOffset] = useState<{ x: number; y: number } | undefined>(undefined);
  const [pointerDocumentPosition, setPointerDocumentPosition] = useState<{ x: number; y: number } | undefined>(undefined);
  const [pointerInsideCanvas, setPointerInsideCanvas] = useState(false);
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });
  const [animationFrameOverlay, setAnimationFrameOverlay] = useState<{ frameId: string; bounds: Rect } | undefined>(undefined);
  const [activeGuideSelection, setActiveGuideSelection] = useState<GuideSelection | undefined>(selectedGuide);
  const cameraRef = useRef(viewStore.getCamera());
  const tileZoomBucketRef = useRef(settledZoomBucketForScale(cameraRef.current.zoom * (window.devicePixelRatio || 1)));
  const documentRef = useRef(document);
  const activePageIdRef = useRef(resolvedActivePageId);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const tileGapsCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const animationCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const previousAnimationFrameOverlayRef = useRef<typeof animationFrameOverlay>(undefined);
  const animationFrameOverlayRef = useRef<typeof animationFrameOverlay>(undefined);
  const syncAnimationCanvasCameraRef = useRef<(camera: Camera, overlay?: typeof animationFrameOverlay) => void>(() => {});
  const animationGraphOverlayRef = useRef<AnimationGraphOverlayHandle | null>(null);
  const workspaceRef = useRef<HTMLElement | null>(null);
  const textInputRef = useRef<HTMLTextAreaElement | null>(null);
  const rendererRef = useRef<Renderer | null>(null);
  const renderControllerRef = useRef<CanvasRenderController | null>(null);
  const buildRenderOverlayRef = useRef<(activeCamera?: Camera, overrides?: RenderOverlayOverrides, renderDocument?: CurvaDocument) => ReturnType<typeof buildRenderOverlay>>(() => ({
    selectedNodeIds: [],
    previews: [],
  }));
  const forceFullSceneRef = useRef<() => boolean>(() => false);
  const tileGapsRendererRef = useRef<Renderer | null>(null);
  const animationRendererRef = useRef<Renderer | null>(null);
  const animationRenderGenerationRef = useRef(0);
  const dragRef = useRef<DragState | null>(null);
  const moveAutopanPointerRef = useRef<{ clientX: number; clientY: number; pointerId: number; altKey: boolean; shiftKey: boolean } | undefined>(undefined);
  const moveAutopanFrameRef = useRef<number | undefined>(undefined);
  const pointerStatusKeyRef = useRef<string | undefined>(undefined);
  const pendingPointerStatusRef = useRef<Parameters<NonNullable<typeof onPointerStatusChange>>[0] | undefined>(undefined);
  const pointerStatusFrameRef = useRef<number | undefined>(undefined);
  const spacePanModifierRef = useRef(false);
  const sampledPointerIdRef = useRef<number | undefined>(undefined);
  const doubleClickBaseContainerRef = useRef<string | undefined>(undefined);
  const lastPointerDownForDoubleClickRef = useRef<{ time: number; x: number; y: number } | undefined>(undefined);
  const [draftShape, setDraftShape] = useState<{ kind: PrimitiveKind; bounds: Rect } | undefined>(undefined);
  const draftPencilNodeRef = useRef<NonNullable<SceneOverlay["draftVectorNetwork"]> | undefined>(undefined);
  const draftComponentInstanceRef = useRef<NonNullable<SceneOverlay["draftComponentInstance"]> | undefined>(undefined);
  const pendingDraftPencilRef = useRef<{ document: CurvaDocument; points: Array<{ x: number; y: number }> } | undefined>(undefined);
  const draftPencilVectorizeRef = useRef<{ source: Array<{ x: number; y: number }>; session: PencilVectorizeSession } | undefined>(undefined);
  const draftPencilFrameRef = useRef<number | undefined>(undefined);
  const hoverStateRef = useRef(createPointerHoverState());
  const measureModifierActiveRef = useRef(false);
  const overlayRenderFrameRef = useRef<number | undefined>(undefined);
  const clearPointerHoverRef = useRef<() => void>(() => {});
  const [visibleRotationOriginNodeId, setVisibleRotationOriginNodeId] = useState<string | undefined>(undefined);
  const [originPreview, setOriginPreview] = useState<{ nodeId: string; x: number; y: number; snapPoints: Array<{ x: number; y: number }> } | undefined>(undefined);
  const [shapeBuilderHoverRegionId, setShapeBuilderHoverRegionId] = useState<string | undefined>(undefined);
  const [shapeBuilderAddedRegionIds, setShapeBuilderAddedRegionIds] = useState<string[]>([]);
  const [shapeBuilderRemovedRegionIds, setShapeBuilderRemovedRegionIds] = useState<string[]>([]);
  const shapeBuilderAddedRegionIdsRef = useRef<string[]>([]);
  const shapeBuilderRemovedRegionIdsRef = useRef<string[]>([]);
  const [knifePreview, setKnifePreview] = useState<KnifePreviewOverlay | undefined>(undefined);
  const [pendingKnifeAroundCut, setPendingKnifeAroundCutState] = useState<{ nodeId: string; vertexId: string; edgeIds: string[] } | undefined>(undefined);
  const pendingKnifeAroundCutRef = useRef<{ nodeId: string; vertexId: string; edgeIds: string[] } | undefined>(undefined);
  const [activeGradientHandle, setActiveGradientHandle] = useState<string | undefined>(undefined);
  const [activeImageHandle, setActiveImageHandle] = useState<ImageFillHandle | undefined>(undefined);
  const [textSelection, setTextSelection] = useState({ anchor: 0, focus: 0 });
  const [colorSamplePreview, setColorSamplePreview] = useState<ColorSamplePreview | undefined>(undefined);
  const [prototypeLinkPreview, setPrototypeLinkPreview] = useState<SelectionOverlay["prototypeLinkPreview"]>(undefined);
  const [animObjectDragGhost, setAnimObjectDragGhost] = useState<{ point: { x: number; y: number }; kind: AnimationNodeKind } | undefined>(undefined);
  const [textInputFocused, setTextInputFocused] = useState(false);
  const [caretVisible, setCaretVisible] = useState(true);
  const [renamingFrameId, setRenamingFrameId] = useState<string | undefined>(undefined);
  const textSelectionRef = useRef(textSelection);
  const textInputFocusedRef = useRef(textInputFocused);
  const textInputCommittedValueRef = useRef("");
  const textUndoStackRef = useRef<Array<{ value: string; selection: { anchor: number; focus: number } }>>([]);
  const textRedoStackRef = useRef<Array<{ value: string; selection: { anchor: number; focus: number } }>>([]);
  const lastReportedTextSelectionRef = useRef<{ nodeId: string; anchor: number; focus: number; focused: boolean } | undefined>(undefined);
  const pendingTextSelectionRef = useRef<{ anchor: number; focus: number } | undefined>(undefined);
  const editableSelectedNodeIds = useMemo(() => editableNodeIds(document, selectedNodeIds), [document, selectedNodeIds]);
  const selectionBounds = previewSelectionBounds(document, editableSelectedNodeIds, previews);
  const inspectorBoundsPreview = previews.some(preview => preview.nodeId !== "__marquee__" && preview.mode !== "copy")
    ? selectionBounds
    : undefined;
  const armedAnimationNodeGhost = animationModeActive && armedAnimationNodeKind && pointerInsideCanvas && pointerDocumentPosition
    ? { point: pointerDocumentPosition, kind: armedAnimationNodeKind }
    : undefined;

  function setPendingKnifeAroundCut(next: { nodeId: string; vertexId: string; edgeIds: string[] } | undefined): void {
    pendingKnifeAroundCutRef.current = next;
    setPendingKnifeAroundCutState(next);
  }
  const selectionTransformBounds = selectionBoundsForTransforms(document, editableSelectedNodeIds, previews);
  const activeShapeBuilderNode = activeTool === "shapeBuilder" && mode === "vectorEdit" && primarySelectedNodeId
    ? getAllSceneNodes(document).find((node): node is Extract<SceneNode, { type: "vectorNetwork" }> => node.id === primarySelectedNodeId && node.type === "vectorNetwork")
    : undefined;
  const shapeBuilderOverlayRegions = useMemo(() => {
    if (!activeShapeBuilderNode) {
      return [];
    }

    const added = new Set(shapeBuilderAddedRegionIds);
    const removed = new Set(shapeBuilderRemovedRegionIds);
    return shapeBuilderRegions(activeShapeBuilderNode.network).flatMap(region => {
      const state: ShapeBuilderRegionOverlay["state"] | undefined = removed.has(region.id)
        ? "remove"
        : added.has(region.id)
          ? "add"
          : shapeBuilderHoverRegionId === region.id
            ? "hover"
            : undefined;
      return state ? [{ ...region, state }] : [];
    });
  }, [activeShapeBuilderNode?.id, activeShapeBuilderNode?.network, shapeBuilderAddedRegionIds, shapeBuilderHoverRegionId, shapeBuilderRemovedRegionIds]);
  const selectedTextNode = getAllSceneNodes(document).find((node): node is Extract<SceneNode, { type: "text" }> => node.type === "text" && node.id === primarySelectedNodeId && !isNodeLockedForEditing(document, node.id));
  const frameLabelItems = useMemo(
    () => frameLabelRenderItems(document, previews, renamingFrameId, resolvedActivePageId, { allowScripts }),
    [resolvedActivePageId, document, previews, renamingFrameId, allowScripts],
  );
  const vectorSmartTransformKey = smartTransformSelectionKey(mode, primarySelectedNodeId, selectedVertexIds);
  const [vectorSmartTransform, setVectorSmartTransform] = useState<{ key: string; rotation: number } | undefined>(undefined);
  const vectorSmartTransformRotation = vectorSmartTransform && vectorSmartTransform.key === vectorSmartTransformKey ? vectorSmartTransform.rotation : 0;
  const vectorSmartTransformBox = useMemo(
    () => vectorSmartTransformOverlay(document, mode, primarySelectedNodeId, selectedVertexIds, vertexPreview, vectorSmartTransformRotation),
    [document, mode, primarySelectedNodeId, selectedVertexIds, vectorSmartTransformRotation, vertexPreview],
  );
  const activeGradient = useMemo(
    () => activeGradientForSelection(document, primarySelectedNodeId, selectedNodeIds, editingGradientFillTarget),
    [document, editingGradientFillTarget, primarySelectedNodeId, selectedNodeIds],
  );
  const activeImageFill = useMemo(
    () => activeImageFillForSelection(document, primarySelectedNodeId, selectedNodeIds, editingImageFillTarget),
    [document, editingImageFillTarget, primarySelectedNodeId, selectedNodeIds],
  );
  const renderTextSelection = useMemo(
    () => selectedTextNode && mode === "textEdit"
      ? { nodeId: selectedTextNode.id, anchor: textSelection.anchor, focus: textSelection.focus, focused: textInputFocused, caretVisible }
      : undefined,
    [caretVisible, mode, selectedTextNode?.id, textInputFocused, textSelection.anchor, textSelection.focus],
  );
  const frameLabelItemsRef = useRef(frameLabelItems);
  frameLabelItemsRef.current = frameLabelItems;
  const renderOverlayRef = useRef<RenderOverlay>({
    selectedNodeIds: [],
    previews: [],
  });
  const wheelIdleTimerRef = useRef<number | undefined>(undefined);
  const pendingZoomStabilizationRef = useRef(false);
  const lastReportedZoomRef = useRef(cameraRef.current.zoom);
  /** Safari trackpad pinch uses gesture* events; ignore concurrent wheel+ctrl to avoid double-zoom. */
  const safariPinchRef = useRef<{ active: boolean; baseZoom: number }>({ active: false, baseZoom: 1 });
  const requestSceneRenderRef = useRef<(mode?: "full" | "camera") => void>(() => {});
  const commitCameraGestureRef = useRef<() => void>(() => {});
  const scheduleCameraCommitRef = useRef<() => void>(() => {});
  documentRef.current = documentStore.getDocument();
  activePageIdRef.current = resolvedActivePageId;

  useEffect(() => {
    setActiveGuideSelection(selectedGuide);
  }, [selectedGuide]);

  function beginGuidePointerDown(event: React.PointerEvent<HTMLCanvasElement>, point: { x: number; y: number }): boolean {
    const guideHit = hitTestGuide(
      collectResolvedGuides(document, resolvedActivePageId, editableSelectedNodeIds),
      point,
      cameraRef.current.zoom,
    );
    if (!guideHit) {
      return false;
    }

    const selection = guideSelectionFromResolved(guideHit);
    setActiveGuideSelection(selection);
    onGuideSelectionChange(selection);
    onSelectionChange([], undefined);
    workspaceRef.current?.setPointerCapture(event.pointerId);
    dragRef.current = {
      mode: "guideMove",
      startClientX: event.clientX,
      startClientY: event.clientY,
      startDocumentX: point.x,
      startDocumentY: point.y,
      startBoundsById: new Map(),
      nodeIds: [],
      guideOrientation: guideHit.orientation,
      guideSelection: selection,
      guideStartPosition: guideHit.position,
      moved: false,
    };
    syncOverlayRender(undefined, { immediate: true });
    return true;
  }

  function createRenderOverlayInput(
    activeCamera = cameraRef.current,
    overrides?: RenderOverlayOverrides,
    renderDocument?: CurvaDocument,
  ) {
    // Read selection/targeting fields from the live stores rather than the React
    // snapshot closures. The render controller redraws synchronously on store
    // changes, which happens before React re-renders this component, so the
    // closures would otherwise be one commit behind (overlay lagging until the
    // next pointer move). `document`, `hover`, and `drag` are already read live.
    const liveSelection = selectionStore.getSelection();
    const liveVectorTargetNodeId = uiStore.animation.getValue().vectorTargetNodeId;
    const livePreviews = viewStore.previews.getValue();
    const liveEditableIds = editableNodeIds(renderDocument ?? documentStore.getDocument(), liveSelection.selectedNodeIds);
    const liveSelectionBounds = previewSelectionBounds(
      renderDocument ?? documentStore.getDocument(),
      liveEditableIds,
      livePreviews,
    );
    return {
      document: renderDocument ?? documentStore.getDocument(),
      camera: activeCamera,
      overrides,
      activePageId: activePageIdRef.current,
      mode: liveSelection.mode,
      activeTool,
      selectedNodeIds: liveSelection.selectedNodeIds,
      editableSelectedNodeIds: liveEditableIds,
      primarySelectedNodeId: liveSelection.primarySelectedNodeId,
      selectedVertexId: liveSelection.selectedVertexId,
      selectedVertexIds: liveSelection.selectedVertexIds ?? [],
      selectedEdgeIds: liveSelection.selectedEdgeIds ?? [],
      selectedHandle: liveSelection.selectedHandle,
      devModeActive,
      prototypeModeActive,
      prototypeLinks: prototypeLinksList,
      selectedPrototypeLinkId,
      hover: hoverStateRef.current,
      drag: dragRef.current,
      measureModifierActive: measureModifierActiveRef.current,
      objectSnapGuides,
      activeGuideSelection,
      guidePreview,
      selectionBounds: liveSelectionBounds,
      draftShape,
      draftVectorNetwork: draftPencilNodeRef.current,
      draftComponentInstance: draftComponentInstanceRef.current,
      knifePreview,
      originPreview,
      prototypeLinkPreview,
      animationFrameOverlay,
      visibleRotationOriginNodeId,
      visibleViewportInsets,
      shapeBuilderOverlayRegions,
      frameLabelItems: frameLabelItemsRef.current,
      vectorSmartTransformBox,
      renderTextSelection,
      textPreviews,
      activeGradientHandle,
      activeImageHandle,
      vectorTargetNodeId: liveVectorTargetNodeId,
    };
  }

  function buildRenderOverlayForWorkspace(
    activeCamera = cameraRef.current,
    overrides?: RenderOverlayOverrides,
    renderDocument?: CurvaDocument,
  ): RenderOverlay {
    return buildRenderOverlay(createRenderOverlayInput(activeCamera, overrides, renderDocument));
  }

  buildRenderOverlayRef.current = buildRenderOverlayForWorkspace;
  forceFullSceneRef.current = () => Boolean(
    uiStore.colorSampler.getValue()
    || animationFrameOverlay
    || uiStore.scriptParamPreview.getValue(),
  );

  function syncCanvasCursor(): void {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }

    const hover = hoverStateRef.current;
    const rotationCursorActive = hover.hoveredRotationHandle !== undefined || dragRef.current?.mode === "rotate";
    const spacePanActive = spacePanModifierRef.current || dragRef.current?.mode === "pan";
    canvas.style.cursor = spacePanActive
      ? (dragRef.current?.mode === "pan" && dragRef.current.moved ? "grabbing" : "grab")
      : devModeActive
        ? "default"
      : rotationCursorActive
      ? ROTATION_CURSOR
      : colorSampler
        ? "crosshair"
        : hover.hoveredFrameLabelId && hover.hoveredFrameLabelScriptAction
          ? "default"
        : hover.hoveredFrameLabelId
          ? "move"
          : hover.hoveredRotationOrigin || dragRef.current?.mode === "origin"
            ? "move"
            : hover.hoveredPrototypeNodeId || dragRef.current?.mode === "prototypeLink"
              ? "crosshair"
            : hover.hoveredGradientHandle || hover.hoveredImageHandle
              ? "move"
              : mode === "vectorEdit" && activeTool === "sculpt"
                ? "none"
              : activeTool === "text" || activeTool === "shape" || activeTool === "path" || activeTool === "pencil" || activeTool === "lasso" || (mode === "vectorEdit" && (activeTool === "fill" || activeTool === "knife" || activeTool === "shapeBuilder"))
                ? "crosshair"
                : "default";
  }

  useEffect(() => {
    syncCanvasCursor();
    if (mode !== "vectorEdit" || activeTool !== "sculpt") {
      viewStore.setSculptBrushPreview(undefined);
    }
  }, [activeTool, mode, colorSampler]);

  useEffect(() => {
    const preview = viewStore.sculptBrushPreview.getValue();
    if (preview && activeTool === "sculpt") {
      viewStore.setSculptBrushPreview({ ...preview, radius: sculptBrush.size / 2 });
    }
  }, [activeTool, sculptBrush.size]);

  useEffect(() => {
    if (activeTool !== "subdivide") {
      return;
    }
    const syncConnect = (event: KeyboardEvent) => {
      const session = uiStore.subdivideSession.getValue();
      if (!session) {
        return;
      }
      const connect = event.shiftKey && session.edgeIds.length >= 2;
      setSubdivideConnect(connect);
    };
    window.addEventListener("keydown", syncConnect);
    window.addEventListener("keyup", syncConnect);
    return () => {
      window.removeEventListener("keydown", syncConnect);
      window.removeEventListener("keyup", syncConnect);
    };
  }, [activeTool]);

  useEffect(() => {
    if (prototypeModeActive) {
      return;
    }
    setPrototypeLinkPreview(undefined);
    setPointerHover({ hoveredPrototypeNodeId: undefined });
    scheduleOverlayRender();
  }, [prototypeModeActive]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }

    const updateSize = () => {
      setCanvasSize({ width: canvas.clientWidth, height: canvas.clientHeight });
    };
    updateSize();
    const observer = new ResizeObserver(updateSize);
    observer.observe(canvas);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!componentPlacement) {
      return;
    }

    const componentId = componentPlacement.componentId;
    let placementStartX = 0;
    let placementStartY = 0;
    let hasMoved = false;

    function updateDraftInstance(clientX: number, clientY: number): void {
      const component = getAllSceneNodes(documentRef.current).find(
        candidate => candidate.id === componentId && candidate.type === "component",
      );
      const canvas = canvasRef.current;
      if (!component || component.type !== "component" || !canvas) {
        draftComponentInstanceRef.current = undefined;
        return;
      }

      const point = screenToDocument(clientX, clientY, canvas, cameraRef.current);
      const centerOffset = {
        x: point.x - (component.bounds.x + component.bounds.width / 2),
        y: point.y - (component.bounds.y + component.bounds.height / 2),
      };
      const instance = createInstanceFromComponent(component, centerOffset);
      draftComponentInstanceRef.current = { node: instance };
    }

    function handlePointerMove(event: PointerEvent): void {
      if (!hasMoved && Math.hypot(event.clientX - placementStartX, event.clientY - placementStartY) >= COMPONENT_DRAG_THRESHOLD) {
        hasMoved = true;
      }
      updateDraftInstance(event.clientX, event.clientY);
      syncOverlayRender(undefined, { immediate: true });
    }

    function isPointerOverCanvas(event: PointerEvent, canvas: HTMLCanvasElement): boolean {
      const target = window.document.elementFromPoint(event.clientX, event.clientY);
      return target === canvas || (target instanceof Node && canvas.contains(target));
    }

    function handlePointerUp(event: PointerEvent): void {
      const canvas = canvasRef.current;
      if (canvas && hasMoved && isPointerOverCanvas(event, canvas)) {
        const point = screenToDocument(event.clientX, event.clientY, canvas, cameraRef.current);
        onComponentPlacementCommit?.(componentId, point);
      }

      draftComponentInstanceRef.current = undefined;
      onComponentPlacementEnd?.();
      syncOverlayRender(undefined, { immediate: true });
    }

    function handlePointerDown(event: PointerEvent): void {
      placementStartX = event.clientX;
      placementStartY = event.clientY;
      hasMoved = false;
      updateDraftInstance(event.clientX, event.clientY);
      syncOverlayRender(undefined, { immediate: true });
    }

    window.addEventListener("pointerdown", handlePointerDown, true);
    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
    window.addEventListener("pointercancel", handlePointerUp);
    return () => {
      window.removeEventListener("pointerdown", handlePointerDown, true);
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
      window.removeEventListener("pointercancel", handlePointerUp);
      draftComponentInstanceRef.current = undefined;
    };
  }, [componentPlacement?.componentId, componentPlacement?.token, onComponentPlacementEnd, onComponentPlacementCommit]);

  function renderActiveScene(
    overlay: RenderOverlay,
    mode: "full" | "camera" = "full",
    options: { immediate?: boolean } = {},
  ): void {
    const tileGapsRenderer = tileGapsRendererRef.current;
    if (tileGapsRenderer) {
      tileGapsRenderer.setCamera(cameraRef.current);
      tileGapsRenderer.setTileZoomBucket(tileZoomBucketRef.current);
      if (options.immediate || mode === "full") {
        tileGapsRenderer.drawImmediate(documentRef.current, overlay, mode);
      } else {
        tileGapsRenderer.scheduleDraw(documentRef.current, overlay, mode);
      }
      return;
    }

    const renderer = rendererRef.current;
    const controller = renderControllerRef.current;
    if (!renderer) {
      return;
    }

    if (options.immediate) {
      controller?.presentScene(overlay, mode, { immediate: true });
      return;
    }

    controller?.presentScene(overlay, mode);
  }

  requestSceneRenderRef.current = (mode: "full" | "camera" = "full") => {
    const overlay = buildRenderOverlayForWorkspace(cameraRef.current);
    renderOverlayRef.current = overlay;
    if (tileGapsRendererRef.current) {
      renderActiveScene(overlay, mode, { immediate: mode === "full" });
      return;
    }
    renderControllerRef.current?.presentScene(overlay, mode, {
      immediate: mode === "full",
    });
  };

  function requestRenderer(mode: "full" | "camera" = "full"): void {
    if (tileGapsRendererRef.current) {
      requestSceneRenderRef.current(mode);
      return;
    }
    renderControllerRef.current?.requestRender(mode);
  }

  function syncOverlayRender(
    overrides?: RenderOverlayOverrides,
    options: { immediate?: boolean; trigger?: string } = {},
  ): void {
    timeDebugStep("syncOverlayRender", options.immediate ? "input" : "raf", options.trigger ?? "", () => {
      if (tileGapsRendererRef.current) {
        const overlay = buildRenderOverlayForWorkspace(cameraRef.current, overrides);
        renderOverlayRef.current = overlay;
        const mode = overlayRequiresFullScene(overlay) ? "full" : "camera";
        renderActiveScene(overlay, mode, options);
        return;
      }

      renderControllerRef.current?.syncOverlayRender(overrides, { ...options, trigger: options.immediate ? "overlay:immediate" : "overlay" });
      if (options.immediate) {
        renderOverlayRef.current = buildRenderOverlayForWorkspace(cameraRef.current, overrides);
      }
    });
  }

  function setPointerHover(patch: Partial<PointerHoverState>): void {
    const prevHoveredNodeId = hoverStateRef.current.hoveredNodeId;
    if (!patchPointerHoverState(hoverStateRef.current, patch)) {
      return;
    }

    timeDebugStep("setPointerHover", "input", "", () => {
      const nextHoveredNodeId = hoverStateRef.current.hoveredNodeId;
      if (nextHoveredNodeId !== prevHoveredNodeId) {
        viewStore.setHoveredNodeId(nextHoveredNodeId);
      }

      syncCanvasCursor();
      scheduleOverlayRender();
    });
  }

  function scheduleOverlayRender(): void {
    if (overlayRenderFrameRef.current !== undefined) {
      return;
    }

    overlayRenderFrameRef.current = requestAnimationFrame(() => {
      wrapRafCallback("raf:workspace:overlay", () => {
        overlayRenderFrameRef.current = undefined;
        syncOverlayRender(undefined, { trigger: "overlay" });
      });
    });
  }

  function updateMeasureModifierState(active: boolean): void {
    if (measureModifierActiveRef.current === active) {
      return;
    }

    measureModifierActiveRef.current = active;
    if (editableSelectedNodeIds.length > 0 && mode === "select" && activeTool === "select") {
      scheduleOverlayRender();
    } else if (mode === "vectorEdit") {
      scheduleOverlayRender();
    }
  }

  function frameLabelViewportBounds(activeCamera = cameraRef.current) {
    const canvas = canvasRef.current;
    return canvas ? viewportBoundsForHitTest(activeCamera, canvas) : undefined;
  }

  function hitTestOptions(extra: {
    deepSelect?: boolean;
    selectedNodeIds?: string[];
    coarseGeometry?: boolean;
  } = {}): {
    activePageId: string | undefined;
    coarseGeometry: boolean;
    viewportBounds: Rect | undefined;
    deepSelect?: boolean;
    selectedNodeIds?: string[];
    selectionPreviews: RenderPreview[];
  } {
    const activeCamera = cameraRef.current;
    const canvas = canvasRef.current;
    const { coarseGeometry, ...rest } = extra;
    return {
      activePageId: resolvedActivePageId,
      coarseGeometry: coarseGeometry ?? coarseHitTestGeometry(activeCamera.zoom, activeCamera.devicePixelRatio),
      viewportBounds: canvas ? viewportBoundsForHitTest(activeCamera, canvas) : undefined,
      selectionPreviews: previews,
      ...rest,
    };
  }

  function commitCameraGesture(): void {
    const nextCamera = cameraRef.current;
    reportViewportCenter(nextCamera);
    onViewportChange(cameraToDocumentViewport(nextCamera));
    if (lastReportedZoomRef.current !== nextCamera.zoom) {
      lastReportedZoomRef.current = nextCamera.zoom;
      onZoomChange(nextCamera.zoom);
    }
    const nextTileBucket = settledZoomBucketForScale(nextCamera.zoom * (window.devicePixelRatio || 1));
    const bucketChanged = nextTileBucket !== tileZoomBucketRef.current;
    if (bucketChanged) {
      tileZoomBucketRef.current = nextTileBucket;
      renderControllerRef.current?.setTileZoomBucket(nextTileBucket);
      tileGapsRendererRef.current?.setTileZoomBucket(nextTileBucket);
    }
    if (pendingZoomStabilizationRef.current) {
      pendingZoomStabilizationRef.current = false;
      renderControllerRef.current?.stabilizeAfterCameraGesture();
      tileGapsRendererRef.current?.stabilizeAfterCameraGesture();
    }
    // Camera mode budgets tile fills during the gesture; always finish coverage on idle
    // (pan as well as zoom — otherwise newly revealed tiles stay blank forever).
    requestSceneRenderRef.current("full");
  }

  commitCameraGestureRef.current = commitCameraGesture;

  function scheduleCameraCommit(): void {
    if (wheelIdleTimerRef.current !== undefined) {
      window.clearTimeout(wheelIdleTimerRef.current);
    }
    wheelIdleTimerRef.current = window.setTimeout(() => {
      wheelIdleTimerRef.current = undefined;
      commitCameraGestureRef.current();
    }, 150);
  }

  scheduleCameraCommitRef.current = scheduleCameraCommit;

  function scheduleDraftPencilRender(points: Array<{ x: number; y: number }>): void {
    pendingDraftPencilRef.current = { document: documentStore.getDocument(), points };
    if (draftPencilFrameRef.current !== undefined) {
      return;
    }

    draftPencilFrameRef.current = requestAnimationFrame(() => {
      wrapRafCallback("raf:workspace:draft-pencil", () => {
        draftPencilFrameRef.current = undefined;
        const pending = pendingDraftPencilRef.current;
        pendingDraftPencilRef.current = undefined;
        draftPencilNodeRef.current = pending ? createDraftPencilNode(pending.document, draftPencilNetworkForPoints(pending.points), pending.points, resolvedActivePageId) : undefined;
        if (pending) {
          rendererRef.current?.scheduleDraw(pending.document, buildRenderOverlayForWorkspace());
        }
      });
    });
  }

  function draftPencilNetworkForPoints(points: Array<{ x: number; y: number }>): VectorNetwork | undefined {
    let draft = draftPencilVectorizeRef.current;
    if (!draft || draft.source !== points || draft.session.rawPointCount > points.length) {
      draft = {
        source: points,
        session: createPencilVectorizeSession(),
      };
      draftPencilVectorizeRef.current = draft;
    }

    appendPencilPointsToSession(draft.session, points);
    return pencilVectorizeSessionToNetwork(draft.session, { idPrefix: "draft_pencil" });
  }

  function clearDraftPencilRender(): void {
    pendingDraftPencilRef.current = undefined;
    if (draftPencilFrameRef.current !== undefined) {
      cancelAnimationFrame(draftPencilFrameRef.current);
      draftPencilFrameRef.current = undefined;
    }
    draftPencilNodeRef.current = undefined;
    draftPencilVectorizeRef.current = undefined;
    rendererRef.current?.scheduleDraw(documentRef.current, renderOverlayRef.current);
  }

  renderOverlayRef.current = buildRenderOverlayForWorkspace(cameraRef.current);

  useEffect(() => {
    const nextCamera = cameraFromDocumentViewport(document.viewport ?? DEFAULT_DOCUMENT_VIEWPORT);
    cameraRef.current = nextCamera;
    viewStore.setCamera(nextCamera);
    tileZoomBucketRef.current = settledZoomBucketForScale(nextCamera.zoom * (window.devicePixelRatio || 1));
    rendererRef.current?.setTileZoomBucket(tileZoomBucketRef.current);
    tileGapsRendererRef.current?.setTileZoomBucket(tileZoomBucketRef.current);
    animationGraphOverlayRef.current?.setCamera(nextCamera);
    syncAnimationCanvasCamera(nextCamera);
    lastReportedZoomRef.current = nextCamera.zoom;
    onZoomChange(nextCamera.zoom);
    onViewportChange(cameraToDocumentViewport(nextCamera));
    reportViewportCenter(nextCamera);
  }, [document.id, document.viewport?.x, document.viewport?.y, document.viewport?.zoom]);

  useEffect(() => {
    if (!colorSampler) {
      setColorSamplePreview(undefined);
    }
  }, [colorSampler]);

  useEffect(() => {
    if (activeTool !== "shapeBuilder" || mode !== "vectorEdit") {
      setShapeBuilderHoverRegionId(undefined);
      setShapeBuilderAddedRegionIds([]);
      setShapeBuilderRemovedRegionIds([]);
      shapeBuilderAddedRegionIdsRef.current = [];
      shapeBuilderRemovedRegionIdsRef.current = [];
      scheduleOverlayRender();
    }
  }, [activeTool, mode, primarySelectedNodeId]);

  useEffect(() => {
    setVectorSmartTransform(current => (current?.key === vectorSmartTransformKey ? current : undefined));
    scheduleOverlayRender();
  }, [vectorSmartTransformKey]);

  useEffect(() => {
    onBoundsPreviewChange(inspectorBoundsPreview);
  }, [inspectorBoundsPreview, onBoundsPreviewChange]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      updateCamera({ ...cameraRef.current, zoom: 1 });
      return;
    }

    const currentCamera = cameraRef.current;
    const rect = canvas.getBoundingClientRect();
    const center = {
      x: currentCamera.x + rect.width / 2 / currentCamera.zoom,
      y: currentCamera.y + rect.height / 2 / currentCamera.zoom,
    };
    updateCamera({
      ...currentCamera,
      zoom: 1,
      x: center.x - rect.width / 2,
      y: center.y - rect.height / 2,
    });
  }, [resetZoomToken]);

  useEffect(() => {
    if (!viewportTarget || !canvasRef.current) {
      return;
    }

    const rect = canvasRef.current.getBoundingClientRect();
    updateCamera({
      ...cameraRef.current,
      x: viewportTarget.x - rect.width / 2 / cameraRef.current.zoom,
      y: viewportTarget.y - rect.height / 2 / cameraRef.current.zoom,
    });
  }, [viewportTarget?.token]);

  useEffect(() => {
    textSelectionRef.current = textSelection;
  }, [textSelection]);

  useEffect(() => {
    textInputFocusedRef.current = textInputFocused;
  }, [textInputFocused]);

  useEffect(() => {
    if (visibleRotationOriginNodeId && !editableSelectedNodeIds.includes(visibleRotationOriginNodeId)) {
      setVisibleRotationOriginNodeId(undefined);
      setPointerHover({ hoveredRotationOrigin: false });
      setOriginPreview(undefined);
      scheduleOverlayRender();
    }
  }, [editableSelectedNodeIds, visibleRotationOriginNodeId]);

  useEffect(() => {
    if (!selectedTextNode) {
      setTextInputFocused(false);
      textInputFocusedRef.current = false;
      lastReportedTextSelectionRef.current = undefined;
      return;
    }

    const end = selectedTextNode.text.characters.length;
    const pending = pendingTextSelectionRef.current;
    pendingTextSelectionRef.current = undefined;
    const next = pending ?? { anchor: end, focus: end };
    if (textInputRef.current) {
      textInputRef.current.value = selectedTextNode.text.characters;
    }
    textInputCommittedValueRef.current = selectedTextNode.text.characters;
    textUndoStackRef.current = [];
    textRedoStackRef.current = [];
    textSelectionRef.current = next;
    setTextSelection(next);
    lastReportedTextSelectionRef.current = undefined;
  }, [selectedTextNode?.id]);

  useEffect(() => {
    const input = textInputRef.current;
    if (!input || !selectedTextNode || mode !== "textEdit" || input.value === selectedTextNode.text.characters) {
      return;
    }

    input.value = selectedTextNode.text.characters;
    textInputCommittedValueRef.current = selectedTextNode.text.characters;
    const cursor = Math.min(textSelectionRef.current.focus, selectedTextNode.text.characters.length);
    setTextareaSelection(input, cursor, cursor);
    updateTextSelection({ anchor: cursor, focus: cursor });
  }, [mode, selectedTextNode?.id, selectedTextNode?.text.characters]);

  useEffect(() => {
    if (mode === "textEdit" && selectedTextNode) {
      const pending = pendingTextSelectionRef.current;
      if (pending) {
        pendingTextSelectionRef.current = undefined;
        textSelectionRef.current = pending;
        setTextSelection(pending);
      }
      const input = textInputRef.current;
      const selection = textSelectionRef.current;
      input?.focus();
      requestAnimationFrame(() => setTextareaSelection(input, selection.anchor, selection.focus));
      updateTextInputFocus(true);
      reportTextSelection(selection, true);
      return;
    }

    textInputRef.current?.blur();
    setTextInputFocused(false);
    textInputFocusedRef.current = false;
    lastReportedTextSelectionRef.current = undefined;
  }, [mode, selectedTextNode?.id]);

  useEffect(() => {
    if (!textInputFocused || textSelection.anchor !== textSelection.focus) {
      setCaretVisible(true);
      return;
    }

    const interval = window.setInterval(() => setCaretVisible(visible => !visible), 530);
    return () => window.clearInterval(interval);
  }, [textInputFocused, textSelection]);

  useEffect(() => {
    setCaretVisible(true);
  }, [textSelection.anchor, textSelection.focus]);

  useEffect(() => {
    if (!renderTextSelection) {
      return;
    }
    scheduleOverlayRender();
  }, [renderTextSelection]);

  function syncAnimationCanvasCamera(nextCamera: Camera, overlay = animationFrameOverlayRef.current): void {
    const animCanvas = animationCanvasRef.current;
    if (!animCanvas) {
      return;
    }
    if (!overlay) {
      animCanvas.style.display = "none";
      return;
    }
    const zoom = nextCamera.zoom;
    // Prefer transform over left/top so Safari/WKWebView composites during continuous pan
    // (CSS left/top on a WebGL canvas often only paints after the next pointer move).
    animCanvas.style.display = "block";
    animCanvas.style.left = "0px";
    animCanvas.style.top = "0px";
    animCanvas.style.width = `${overlay.bounds.width * zoom}px`;
    animCanvas.style.height = `${overlay.bounds.height * zoom}px`;
    animCanvas.style.transform = `translate(${(overlay.bounds.x - nextCamera.x) * zoom}px, ${(overlay.bounds.y - nextCamera.y) * zoom}px)`;
  }
  syncAnimationCanvasCameraRef.current = syncAnimationCanvasCamera;

  function updateCamera(nextCamera: Camera, options: { zoomGesture?: boolean } = {}): void {
    closePanelFloatingUi();
    if (options.zoomGesture || nextCamera.zoom !== cameraRef.current.zoom) {
      pendingZoomStabilizationRef.current = true;
    }
    cameraRef.current = nextCamera;
    viewStore.setCamera(nextCamera);
    renderControllerRef.current?.setCamera(nextCamera);
    tileGapsRendererRef.current?.setCamera(nextCamera);
    animationGraphOverlayRef.current?.setCamera(nextCamera);
    syncAnimationCanvasCamera(nextCamera);
    // Keep the tile bucket frozen and use camera-only renders during the gesture.
    // Advancing the bucket here triggers full tile re-rasterization per wheel tick and
    // can freeze the page when zoom speed is high.
    // Viewport center/bounds are for paste placement — only refresh on gesture idle.
    scheduleCameraCommit();
  }

  function panCameraByScreenDelta(delta: { x: number; y: number }): void {
    const zoom = cameraRef.current.zoom;
    updateCamera({
      ...cameraRef.current,
      x: cameraRef.current.x + delta.x / zoom,
      y: cameraRef.current.y + delta.y / zoom,
    });
  }

  function updateMovePreviewFromPointer(pointer: { clientX: number; clientY: number; altKey: boolean; shiftKey: boolean }): void {
    const canvas = canvasRef.current;
    const drag = dragRef.current;
    if (!canvas || drag?.mode !== "move") {
      return;
    }

    const point = screenToDocument(pointer.clientX, pointer.clientY, canvas, cameraRef.current);
    const editPoint = snapToPixelGrid ? snapPointToPixelGrid(point) : point;
    const dx = editPoint.x - drag.startDocumentX;
    const dy = editPoint.y - drag.startDocumentY;
    drag.lastDx = dx;
    drag.lastDy = dy;
    drag.moved = true;

    const flexReorder = pointer.altKey ? undefined : flexReorderPreviewForDrag(documentRef.current, drag, dx, dy);
    drag.flexReorderTarget = flexReorder?.target;
    const snap = flexReorder ?? movePreviewsForDrag(
      drag,
      dx,
      dy,
      pointer.altKey,
      snapToPixelGrid,
      snapToObjects,
      snapToGuides,
      cameraRef.current.zoom,
      documentRef.current,
      activePageIdRef.current,
      editableSelectedNodeIds,
      pointer.shiftKey,
    );
    viewStore.setPreviews(snap.previews);
    setObjectSnapGuides(snap.guides);
    syncOverlayRender({ objectSnapGuides: snap.guides }, { trigger: "pointer:movePreview" });
  }

  function startMoveAutopan(): void {
    if (moveAutopanFrameRef.current !== undefined) {
      return;
    }
    const tick = () => {
      moveAutopanFrameRef.current = undefined;
      const pointer = moveAutopanPointerRef.current;
      const canvas = canvasRef.current;
      const drag = dragRef.current;
      if (!pointer || !canvas || drag?.mode !== "move") {
        return;
      }

      if (drag.moved) {
        const delta = dragAutopanDelta(pointer, canvas.getBoundingClientRect(), visibleViewportInsets ?? { top: 0, right: 0, bottom: 0, left: 0 });
        if (delta.x !== 0 || delta.y !== 0) {
          panCameraByScreenDelta(delta);
          updateMovePreviewFromPointer(pointer);
        }
      }
      moveAutopanFrameRef.current = window.requestAnimationFrame(tick);
    };
    moveAutopanFrameRef.current = window.requestAnimationFrame(tick);
  }

  function stopMoveAutopan(): void {
    moveAutopanPointerRef.current = undefined;
    if (moveAutopanFrameRef.current !== undefined) {
      window.cancelAnimationFrame(moveAutopanFrameRef.current);
      moveAutopanFrameRef.current = undefined;
    }
  }

  function updatePointerStatus(clientX: number, clientY: number): void {
    const canvas = canvasRef.current;
    if (!canvas || !onPointerStatusChange) {
      return;
    }

    const insets = visibleViewportInsets ?? { top: 0, right: 0, bottom: 0, left: 0 };
    if (!pointInsideCanvasContentArea(clientX, clientY, canvas, insets)) {
      clearPointerStatus();
      return;
    }

    const point = screenToDocument(clientX, clientY, canvas, cameraRef.current);
    const viewportBounds = viewportBoundsForHitTest(cameraRef.current, canvas);
    const frame = timeDebugStep("findDeepestFrameAtPoint", "input", "pointer-status", () =>
      findDeepestFrameAtPoint(documentRef.current, point, activePageIdRef.current, viewportBounds));
    const framePosition = frame ? { x: point.x - frame.bounds.x, y: point.y - frame.bounds.y } : undefined;
    const statusKey = `${formatPointerStatusValue(point.x)},${formatPointerStatusValue(point.y)}:${frame?.id ?? ""}:${framePosition ? `${formatPointerStatusValue(framePosition.x)},${formatPointerStatusValue(framePosition.y)}` : ""}`;
    if (pointerStatusKeyRef.current === statusKey) {
      return;
    }
    pointerStatusKeyRef.current = statusKey;
    schedulePointerStatusChange({
      canvas: point,
      frame: frame
        ? {
            frameId: frame.id,
            frameName: frame.name,
            position: framePosition!,
          }
        : undefined,
    });
  }

  function clearPointerStatus(): void {
    if (pointerStatusKeyRef.current === undefined) {
      return;
    }
    pointerStatusKeyRef.current = undefined;
    schedulePointerStatusChange(undefined);
  }

  function schedulePointerStatusChange(status: Parameters<NonNullable<typeof onPointerStatusChange>>[0] | undefined): void {
    pendingPointerStatusRef.current = status;
    if (pointerStatusFrameRef.current !== undefined) {
      return;
    }
    pointerStatusFrameRef.current = window.requestAnimationFrame(() => {
      wrapRafCallback("raf:workspace:pointer-status", () => {
        pointerStatusFrameRef.current = undefined;
        const pending = pendingPointerStatusRef.current;
        pendingPointerStatusRef.current = undefined;
        onPointerStatusChange?.(pending);
      });
    });
  }

  function formatPointerStatusValue(value: number): string {
    return value.toFixed(1);
  }

  function reportViewportCenter(nextCamera = cameraRef.current): void {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }

    const rect = canvas.getBoundingClientRect();
    viewStore.setViewportCenter({
      x: nextCamera.x + rect.width / 2 / nextCamera.zoom,
      y: nextCamera.y + rect.height / 2 / nextCamera.zoom,
    });
    viewStore.setViewportBounds({
      x: nextCamera.x,
      y: nextCamera.y,
      width: rect.width / nextCamera.zoom,
      height: rect.height / nextCamera.zoom,
    });
  }

  function reportTextSelection(next: { anchor: number; focus: number }, focused: boolean): void {
    if (!selectedTextNode || mode !== "textEdit") {
      return;
    }

    const report = { nodeId: selectedTextNode.id, anchor: next.anchor, focus: next.focus, focused };
    const previous = lastReportedTextSelectionRef.current;
    if (
      previous?.nodeId === report.nodeId &&
      previous.anchor === report.anchor &&
      previous.focus === report.focus &&
      previous.focused === report.focused
    ) {
      return;
    }

    lastReportedTextSelectionRef.current = report;
    onTextSelectionChange(report);
  }

  function updateTextSelection(next: { anchor: number; focus: number }): void {
    const current = textSelectionRef.current;
    if (current.anchor === next.anchor && current.focus === next.focus) {
      return;
    }
    textSelectionRef.current = next;
    setTextSelection(current => {
      if (current.anchor === next.anchor && current.focus === next.focus) {
        return current;
      }
      return next;
    });
    reportTextSelection(next, textInputFocusedRef.current);
    scheduleOverlayRender();
  }

  function updateTextInputFocus(focused: boolean): void {
    if (textInputFocusedRef.current === focused) {
      return;
    }
    textInputFocusedRef.current = focused;
    setTextInputFocused(current => {
      if (current === focused) {
        return current;
      }
      return focused;
    });
    reportTextSelection(textSelectionRef.current, focused);
    scheduleOverlayRender();
  }

  useEffect(() => {
    const workspace = workspaceRef.current;
    if (!workspace) {
      return;
    }

    const applyLiveZoomAroundClient = (clientX: number, clientY: number, nextZoom: number): void => {
      const canvas = canvasRef.current;
      if (!canvas) {
        return;
      }

      const currentCamera = cameraRef.current;
      const zoom = clampZoom(nextZoom);
      const canvasRect = canvas.getBoundingClientRect();
      const before = screenToDocument(clientX, clientY, canvas, currentCamera);
      const nextCamera = {
        ...currentCamera,
        zoom,
        x: before.x - (clientX - canvasRect.left) / zoom,
        y: before.y - (clientY - canvasRect.top) / zoom,
      };
      pendingZoomStabilizationRef.current = true;
      cameraRef.current = nextCamera;
      viewStore.setCamera(nextCamera);
      renderControllerRef.current?.setCamera(nextCamera);
      tileGapsRendererRef.current?.setCamera(nextCamera);
      animationGraphOverlayRef.current?.setCamera(nextCamera);
      syncAnimationCanvasCameraRef.current(nextCamera);
      reportViewportCenter(nextCamera);
      scheduleCameraCommitRef.current();
    };

    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      const canvas = canvasRef.current;
      if (!canvas) {
        return;
      }

      const deltaX = normalizeWheelDelta(event.deltaX, event.deltaMode);
      const deltaY = normalizeWheelDelta(event.deltaY, event.deltaMode);
      const currentCamera = cameraRef.current;

      // Chrome/Firefox (and Safari 15+ sometimes) map trackpad pinch to wheel+ctrl.
      // While a Safari gesture* pinch is active, skip to avoid applying zoom twice.
      if (event.ctrlKey) {
        if (safariPinchRef.current.active) {
          return;
        }
        applyLiveZoomAroundClient(
          event.clientX,
          event.clientY,
          currentCamera.zoom * Math.exp(-deltaY * 0.01 * WHEEL_ZOOM_SPEED),
        );
        return;
      }

      const nextCamera = {
        ...currentCamera,
        x: currentCamera.x + (deltaX * WHEEL_PAN_SPEED) / currentCamera.zoom,
        y: currentCamera.y + (deltaY * WHEEL_PAN_SPEED) / currentCamera.zoom,
      };
      cameraRef.current = nextCamera;
      viewStore.setCamera(nextCamera);
      renderControllerRef.current?.setCamera(nextCamera);
      tileGapsRendererRef.current?.setCamera(nextCamera);
      animationGraphOverlayRef.current?.setCamera(nextCamera);
      syncAnimationCanvasCameraRef.current(nextCamera);
      reportViewportCenter(nextCamera);
      scheduleCameraCommitRef.current();
    };

    // Safari trackpad pinch: proprietary gesture events (scale is cumulative from gesturestart).
    // useBrowserGestureGuards already preventDefault's these to stop page zoom; we still need to
    // drive the canvas camera here because wheel+ctrl is not reliable on Safari for pinch.
    const onGestureStart = (event: Event) => {
      if (event.cancelable) {
        event.preventDefault();
      }
      safariPinchRef.current = { active: true, baseZoom: cameraRef.current.zoom };
    };

    const onGestureChange = (event: Event) => {
      const gesture = event as SafariGestureEvent;
      if (event.cancelable) {
        event.preventDefault();
      }
      if (!safariPinchRef.current.active) {
        safariPinchRef.current = { active: true, baseZoom: cameraRef.current.zoom };
      }
      const scale = Number.isFinite(gesture.scale) && gesture.scale > 0 ? gesture.scale : 1;
      applyLiveZoomAroundClient(
        gesture.clientX,
        gesture.clientY,
        safariPinchRef.current.baseZoom * scale,
      );
    };

    const onGestureEnd = (event: Event) => {
      if (event.cancelable) {
        event.preventDefault();
      }
      safariPinchRef.current = { active: false, baseZoom: cameraRef.current.zoom };
      scheduleCameraCommitRef.current();
    };

    workspace.addEventListener("wheel", onWheel, { passive: false });
    workspace.addEventListener("gesturestart", onGestureStart, { passive: false });
    workspace.addEventListener("gesturechange", onGestureChange, { passive: false });
    workspace.addEventListener("gestureend", onGestureEnd, { passive: false });
    return () => {
      workspace.removeEventListener("wheel", onWheel);
      workspace.removeEventListener("gesturestart", onGestureStart);
      workspace.removeEventListener("gesturechange", onGestureChange);
      workspace.removeEventListener("gestureend", onGestureEnd);
    };
  }, []);

  useEffect(() => {
    const handleMeasureModifierChange = (event: KeyboardEvent) => {
      if (event.key !== "Alt" && event.key !== "Shift") {
        return;
      }

      updateMeasureModifierState(isMeasureModifierActive(event));
    };

    window.addEventListener("keydown", handleMeasureModifierChange);
    window.addEventListener("keyup", handleMeasureModifierChange);
    return () => {
      window.removeEventListener("keydown", handleMeasureModifierChange);
      window.removeEventListener("keyup", handleMeasureModifierChange);
    };
  }, [editableSelectedNodeIds, mode, activeTool]);

  useEffect(() => {
    if (editableSelectedNodeIds.length > 0 && mode === "select" && activeTool === "select") {
      scheduleOverlayRender();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [devModeActive]);

  useEffect(() => {
    const handleSpacePanKeyDown = (event: KeyboardEvent) => {
      if (event.code !== "Space" && event.key !== " ") {
        return;
      }
      if (isEditableShortcutTarget(event.target) || mode === "textEdit") {
        return;
      }
      if (animationModeActive) {
        return;
      }
      if (event.repeat) {
        return;
      }
      event.preventDefault();
      if (!spacePanModifierRef.current) {
        spacePanModifierRef.current = true;
        syncCanvasCursor();
        if (!dragRef.current) {
          clearPointerHoverRef.current();
        }
      }
    };

    const handleSpacePanKeyUp = (event: KeyboardEvent) => {
      if (event.code !== "Space" && event.key !== " ") {
        return;
      }
      if (!spacePanModifierRef.current) {
        return;
      }
      spacePanModifierRef.current = false;
      syncCanvasCursor();
    };

    window.addEventListener("keydown", handleSpacePanKeyDown, true);
    window.addEventListener("keyup", handleSpacePanKeyUp, true);
    return () => {
      window.removeEventListener("keydown", handleSpacePanKeyDown, true);
      window.removeEventListener("keyup", handleSpacePanKeyUp, true);
    };
  }, [animationModeActive, mode]);

  useEffect(() => {
    const handleModifierChange = (event: KeyboardEvent) => {
      const drag = dragRef.current;
      if (drag?.mode !== "move" || !drag.moved || drag.lastDx === undefined || drag.lastDy === undefined) {
        return;
      }

      const flexReorder = event.altKey ? undefined : flexReorderPreviewForDrag(document, drag, drag.lastDx, drag.lastDy);
      drag.flexReorderTarget = flexReorder?.target;
      const snap = flexReorder ?? movePreviewsForDrag(
        drag,
        drag.lastDx,
        drag.lastDy,
        event.altKey,
        snapToPixelGrid,
        snapToObjects,
        snapToGuides,
        cameraRef.current.zoom,
        document,
        resolvedActivePageId,
        editableSelectedNodeIds,
        event.shiftKey,
      );
      viewStore.setPreviews(snap.previews);
      setObjectSnapGuides(snap.guides);
    };

    window.addEventListener("keydown", handleModifierChange);
    window.addEventListener("keyup", handleModifierChange);
    return () => {
      window.removeEventListener("keydown", handleModifierChange);
      window.removeEventListener("keyup", handleModifierChange);
    };
  }, [document, snapToObjects, snapToPixelGrid]);

  useEffect(() => {
    const handleShiftRelease = (event: KeyboardEvent) => {
      if (event.key !== "Shift") {
        return;
      }

      const pending = pendingKnifeAroundCutRef.current;
      if (!pending) {
        return;
      }

      pendingKnifeAroundCutRef.current = undefined;
      setPendingKnifeAroundCutState(undefined);
      setKnifePreview(undefined);
      onKnifeCutAroundVertex(pending.nodeId, pending.vertexId, pending.edgeIds);
    };

    window.addEventListener("keyup", handleShiftRelease);
    return () => window.removeEventListener("keyup", handleShiftRelease);
  }, [onKnifeCutAroundVertex]);

  useEffect(() => {
    const pending = pendingKnifeAroundCutRef.current;
    if (
      !pending ||
      (mode === "vectorEdit" &&
        activeTool === "knife" &&
        primarySelectedNodeId === pending.nodeId &&
        selectedVertexIds.length === 1 &&
        selectedVertexIds[0] === pending.vertexId)
    ) {
      return;
    }

    pendingKnifeAroundCutRef.current = undefined;
    setPendingKnifeAroundCutState(undefined);
    setKnifePreview(current => (current?.mode === "around" ? undefined : current));
    scheduleOverlayRender();
  }, [activeTool, mode, primarySelectedNodeId, selectedVertexIds]);

  useEffect(() => {
    if (!canvasRef.current) {
      return;
    }

    const controller = new CanvasRenderController({
      canvas: canvasRef.current,
      rendererOptions: { preserveDrawingBuffer: true },
      buildOverlay: ({ camera: activeCamera, document: renderDocument, overrides }) =>
        buildRenderOverlayRef.current(activeCamera ?? cameraRef.current, overrides, renderDocument),
      forceFullScene: () => forceFullSceneRef.current(),
      onOverlayBuilt: overlay => {
        renderOverlayRef.current = overlay;
      },
      subscribeToStores: true,
    });
    renderControllerRef.current = controller;
    rendererRef.current = controller.getRenderer();
    controller.setProfilerEnabled(Boolean(showRenderDebug));
    controller.setProfilerOverlayVisible(Boolean(showRenderDebug));
    setRenderDebugEnabled(Boolean(showRenderDebug));
    controller.setRenderTrianglesDebug(Boolean(renderTriangles));
    controller.setTileZoomBucket(tileZoomBucketRef.current);
    controller.setCamera(cameraRef.current);
    reportViewportCenter(cameraRef.current);
    renderOverlayRef.current = buildRenderOverlayForWorkspace(cameraRef.current);
    controller.invalidateAll(document);
    controller.renderNow("full", undefined, { trigger: "mount" });
    syncCanvasCursor();

    return () => {
      if (draftPencilFrameRef.current !== undefined) {
        cancelAnimationFrame(draftPencilFrameRef.current);
        draftPencilFrameRef.current = undefined;
      }
      if (pointerStatusFrameRef.current !== undefined) {
        cancelAnimationFrame(pointerStatusFrameRef.current);
        pointerStatusFrameRef.current = undefined;
      }
      controller.dispose();
      renderControllerRef.current = null;
      rendererRef.current = null;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const controller = renderControllerRef.current;

    void (async () => {
      if (useWasmTessellation) {
        const enabled = await enableWasmMeshKernels({ force: true });
        if (cancelled) {
          return;
        }
        if (!enabled) {
          uiStore.patchCanvasDebug({ useWasmTessellation: false });
          uiStore.setMessage("Wasm tessellation failed to load");
          return;
        }
      } else {
        disableWasmMeshKernels();
      }

      getGeometryPool().clear();
      if (cancelled || !controller || renderControllerRef.current !== controller) {
        return;
      }
      controller.invalidateAll(documentStore.getDocument());
      const overlay = buildRenderOverlayForWorkspace();
      renderOverlayRef.current = overlay;
      controller.presentScene(overlay, "full", { immediate: true });
    })();

    return () => {
      cancelled = true;
    };
  }, [useWasmTessellation]);

  useEffect(() => {
    const canvas = animationCanvasRef.current;
    if (scriptParamPreview) {
      // Script-param ghost owns the canvas while the params modal is previewing.
      return;
    }
    if (!animationModeActive || animationTimeMs <= 0 || !animationGraph || !canvas) {
      animationRenderGenerationRef.current += 1;
      animationFrameOverlayRef.current = undefined;
      setAnimationFrameOverlay(undefined);
      syncAnimationCanvasCamera(cameraRef.current, undefined);
      scheduleOverlayRender();
      return;
    }

    const generation = animationRenderGenerationRef.current + 1;
    animationRenderGenerationRef.current = generation;
    const renderer = animationRendererRef.current ?? new Renderer(canvas, { alpha: false, preserveDrawingBuffer: true });
    animationRendererRef.current = renderer;

    const scale = Math.max(1, cameraRef.current.zoom * cameraRef.current.devicePixelRatio);
    void renderAnimationPreviewToCanvas(renderer, {
      document,
      animationGraph,
      activeAnimationRootId,
      animationFrames,
      timeMs: animationTimeMs,
      scale,
    }).then(result => {
      if (animationRenderGenerationRef.current !== generation) {
        return;
      }
      const next = result ? { frameId: result.frameId, bounds: result.bounds } : undefined;
      animationFrameOverlayRef.current = next;
      setAnimationFrameOverlay(next);
      syncAnimationCanvasCamera(cameraRef.current, next);
      scheduleOverlayRender();
    }).catch(error => {
      if (animationRenderGenerationRef.current === generation) {
        console.warn("Failed to render animation frame", error);
        animationFrameOverlayRef.current = undefined;
        setAnimationFrameOverlay(undefined);
        syncAnimationCanvasCamera(cameraRef.current, undefined);
        scheduleOverlayRender();
      }
    });
  }, [activeAnimationRootId, animationFrames, animationGraph, animationModeActive, animationTimeMs, document, scriptParamPreview]);

  useEffect(() => {
    const canvas = animationCanvasRef.current;
    if (!scriptParamPreview || !canvas) {
      if (!scriptParamPreview && !(animationModeActive && animationTimeMs > 0 && animationGraph)) {
        animationRenderGenerationRef.current += 1;
        animationFrameOverlayRef.current = undefined;
        setAnimationFrameOverlay(undefined);
        syncAnimationCanvasCamera(cameraRef.current, undefined);
        scheduleOverlayRender();
      }
      return;
    }

    const generation = animationRenderGenerationRef.current + 1;
    animationRenderGenerationRef.current = generation;
    const renderer = animationRendererRef.current ?? new Renderer(canvas, { alpha: false, preserveDrawingBuffer: true });
    animationRendererRef.current = renderer;

    const previewDocument = materializeScriptPreviewDocument(document, scriptParamPreview.pages);
    const scale = Math.max(1, cameraRef.current.zoom * cameraRef.current.devicePixelRatio);
    void renderScriptPreviewToCanvas(renderer, {
      document: previewDocument,
      frameId: scriptParamPreview.frameId,
      scale,
    }).then(result => {
      if (animationRenderGenerationRef.current !== generation) {
        return;
      }
      const next = result ? { frameId: result.frameId, bounds: result.bounds } : undefined;
      animationFrameOverlayRef.current = next;
      setAnimationFrameOverlay(next);
      syncAnimationCanvasCamera(cameraRef.current, next);
      scheduleOverlayRender();
    }).catch(error => {
      if (animationRenderGenerationRef.current === generation) {
        console.warn("Failed to render script param preview", error);
        animationFrameOverlayRef.current = undefined;
        setAnimationFrameOverlay(undefined);
        syncAnimationCanvasCamera(cameraRef.current, undefined);
        scheduleOverlayRender();
      }
    });
  }, [
    animationGraph,
    animationModeActive,
    animationTimeMs,
    document,
    scriptParamPreview,
    scriptParamPreview?.frameId,
    scriptParamPreview?.revision,
  ]);

  useEffect(() => {
    return () => {
      animationRendererRef.current?.dispose();
      animationRendererRef.current = null;
    };
  }, []);

  useEffect(() => {
    const previous = previousAnimationFrameOverlayRef.current;
    previousAnimationFrameOverlayRef.current = animationFrameOverlay;
    animationFrameOverlayRef.current = animationFrameOverlay;
    syncAnimationCanvasCamera(cameraRef.current, animationFrameOverlay);
    if (previous && !animationFrameOverlay) {
      const controller = renderControllerRef.current;
      controller?.invalidateAll(documentRef.current);
      const overlay = buildRenderOverlayForWorkspace();
      renderOverlayRef.current = overlay;
      controller?.presentScene(overlay, "full", { immediate: true });
    }
  }, [animationFrameOverlay]);

  useEffect(() => {
    setRenderDebugEnabled(Boolean(showRenderDebug));
    renderControllerRef.current?.setProfilerEnabled(Boolean(showRenderDebug));
    renderControllerRef.current?.setProfilerOverlayVisible(Boolean(showRenderDebug));
  }, [showRenderDebug]);

  useEffect(() => {
    const controller = renderControllerRef.current;
    if (!controller) {
      return;
    }

    controller.setRenderTrianglesDebug(Boolean(renderTriangles));
    controller.invalidateAll(documentRef.current);
    const overlay = buildRenderOverlayForWorkspace();
    renderOverlayRef.current = overlay;
    controller.presentScene(overlay, "full", { immediate: true });
  }, [renderTriangles]);

  useEffect(() => {
    if (!showTileGaps) {
      tileGapsRendererRef.current?.dispose();
      tileGapsRendererRef.current = null;
      return;
    }

    const canvas = tileGapsCanvasRef.current;
    if (!canvas) {
      return;
    }

    let disposed = false;
    let renderer: Renderer | null = null;
    const frameId = requestAnimationFrame(() => {
      if (disposed || !tileGapsCanvasRef.current) {
        return;
      }

      renderer = new Renderer(tileGapsCanvasRef.current, { preserveDrawingBuffer: true });
      renderer.setTileGapsDebug(true);
      renderer.setTileZoomBucket(tileZoomBucketRef.current);
      renderer.setCamera(cameraRef.current);
      tileGapsRendererRef.current = renderer;
      const overlay = buildRenderOverlayForWorkspace(cameraRef.current);
      renderOverlayRef.current = overlay;
      renderer.drawImmediate(documentRef.current, overlay, "full");
    });

    return () => {
      disposed = true;
      cancelAnimationFrame(frameId);
      renderer?.dispose();
      tileGapsRendererRef.current = null;
      requestSceneRenderRef.current("full");
    };
  }, [showTileGaps]);

  useEffect(() => {
    if (!showTileGaps) {
      return;
    }

    const tileGapsRenderer = tileGapsRendererRef.current;
    if (!tileGapsRenderer) {
      return;
    }

    const overlay = buildRenderOverlayForWorkspace(cameraRef.current);
    renderOverlayRef.current = overlay;
    tileGapsRenderer.setCamera(cameraRef.current);
    tileGapsRenderer.setTileZoomBucket(tileZoomBucketRef.current);
    tileGapsRenderer.scheduleDraw(documentRef.current, overlay, "camera");
  }, [showTileGaps]);

  useEffect(() => {
    const controller = renderControllerRef.current;
    if (!controller || fontsReadyToken === 0) {
      return;
    }

    controller.invalidateTextRendering();
    const overlay = buildRenderOverlayForWorkspace();
    renderOverlayRef.current = overlay;
    controller.presentScene(overlay, "full");
  }, [fontsReadyToken]);

  useEffect(() => {
    if (!canvasRef.current) {
      return;
    }

    let frameId = 0;
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(frameId);
      frameId = requestAnimationFrame(() => {
        if (showTileGaps) {
          const overlay = buildRenderOverlayForWorkspace(cameraRef.current);
          renderOverlayRef.current = overlay;
          renderActiveScene(overlay, "full", { immediate: true });
          return;
        }

        renderControllerRef.current?.setCamera(cameraRef.current);
        reportViewportCenter(cameraRef.current);
        renderControllerRef.current?.invalidateAll(documentRef.current);
        renderControllerRef.current?.presentScene(renderOverlayRef.current, "full");
      });
    });

    observer.observe(canvasRef.current);
    return () => {
      cancelAnimationFrame(frameId);
      observer.disconnect();
    };
  }, [showTileGaps]);

  function applyShapeBuilderRegionSelection(regionId: string, mode: "add" | "remove"): void {
    if (mode === "remove") {
      shapeBuilderAddedRegionIdsRef.current = shapeBuilderAddedRegionIdsRef.current.filter(id => id !== regionId);
      shapeBuilderRemovedRegionIdsRef.current = shapeBuilderRemovedRegionIdsRef.current.includes(regionId)
        ? shapeBuilderRemovedRegionIdsRef.current
        : [...shapeBuilderRemovedRegionIdsRef.current, regionId];
      setShapeBuilderAddedRegionIds(shapeBuilderAddedRegionIdsRef.current);
      setShapeBuilderRemovedRegionIds(shapeBuilderRemovedRegionIdsRef.current);
      scheduleOverlayRender();
      return;
    }

    shapeBuilderRemovedRegionIdsRef.current = shapeBuilderRemovedRegionIdsRef.current.filter(id => id !== regionId);
    shapeBuilderAddedRegionIdsRef.current = shapeBuilderAddedRegionIdsRef.current.includes(regionId)
      ? shapeBuilderAddedRegionIdsRef.current
      : [...shapeBuilderAddedRegionIdsRef.current, regionId];
    setShapeBuilderRemovedRegionIds(shapeBuilderRemovedRegionIdsRef.current);
    setShapeBuilderAddedRegionIds(shapeBuilderAddedRegionIdsRef.current);
    scheduleOverlayRender();
  }

  function resetShapeBuilderSelection(): void {
    shapeBuilderAddedRegionIdsRef.current = [];
    shapeBuilderRemovedRegionIdsRef.current = [];
    setShapeBuilderHoverRegionId(undefined);
    setShapeBuilderAddedRegionIds([]);
    setShapeBuilderRemovedRegionIds([]);
    scheduleOverlayRender();
  }

  function updateDoubleClickBaseFromSelection(nextSelection: string[]): void {
    const primaryNode = getAllSceneNodes(document).find(node => node.id === nextSelection.at(-1));
    doubleClickBaseContainerRef.current = mode === "select" && activeTool === "select" && primaryNode && isDoubleClickContainer(primaryNode)
      ? primaryNode.id
      : undefined;
  }


  const pointerRouter = createPointerRouter({
    dragRef,
    canvasRef,
    cameraRef,
    documentRef,
    hoverStateRef,
    spacePanModifierRef,
    sampledPointerIdRef,
    doubleClickBaseContainerRef,
    lastPointerDownForDoubleClickRef,
    moveAutopanPointerRef,
    shapeBuilderAddedRegionIdsRef,
    shapeBuilderRemovedRegionIdsRef,
    pendingKnifeAroundCutRef,
    textInputRef,
    textSelectionRef,
    renderOverlayRef,
    rendererRef,
    camera: cameraRef.current,
    previews,
    mode,
    activeTool,
    selectedShapeKind,
    selectedNodeIds,
    primarySelectedNodeId,
    selectedVertexId,
    selectedVertexIds,
    selectedEdgeIds,
    selectedHandle,
    editableSelectedNodeIds,
    selectionBounds,
    selectionTransformBounds,
    resolvedActivePageId,
    snapToPixelGrid,
    snapToObjects,
    snapToGuides,
    snapToGeometry,
    assistedPencilTracing,
    devModeActive,
    prototypeModeActive,
    animationModeActive,
    armedAnimationNodeKind,
    vectorTargetNodeId,
    colorSampler,
    activeGradient,
    activeImageFill,
    vectorSmartTransformBox,
    vectorSmartTransformKey,
    prototypeLinksList,
    selectedTextNode,
    selectedAnimationNodeIds,
    animationGraph,
    visibleViewportInsets,
    visibleRotationOriginNodeId,
    shapeBuilderHoverRegionId,
    vertexPreview,
    handlePreview,
    edgeBendPreview,
    originPreview,
    pendingKnifeAroundCut,
    closePanelFloatingUi,
    syncCanvasCursor,
    syncOverlayRender,
    scheduleOverlayRender,
    setPointerHover,
    beginGuidePointerDown,
    hitTestOptions,
    buildRenderOverlayForWorkspace,
    clearDraftPencilRender,
    scheduleDraftPencilRender,
    updateCamera,
    startMoveAutopan,
    stopMoveAutopan,
    updatePointerStatus,
    clearPointerStatus,
    applyShapeBuilderRegionSelection,
    resetShapeBuilderSelection,
    updateTextSelection,
    setPendingKnifeAroundCut,
    commitCameraGesture,
    updateDoubleClickBaseFromSelection,
    updateMeasureModifierState,
    setDraftShape,
    setObjectSnapGuides,
    setGuidePreview,
    setShapeBuilderHoverRegionId,
    setShapeBuilderAddedRegionIds,
    setShapeBuilderRemovedRegionIds,
    setKnifePreview,
    setActiveGradientHandle,
    setActiveImageHandle,
    setPrototypeLinkPreview,
    setAnimObjectDragGhost,
    setColorSamplePreview,
    setPointerCanvasOffset,
    setPointerInsideCanvas,
    setPointerDocumentPosition,
    setOriginPreview,
    setVectorSmartTransform,
    setRenamingFrameId,
    setVisibleRotationOriginNodeId,
    setActiveGuideSelection,
    documentActions,
    vectorActions,
    animationActions,
    onToolChange,
  });

  const {
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
    handlePointerCancel,
    handlePointerLeave,
    clearPointerHover,
    handleFrameLabelPointerDown,
    updateDoubleClickBaseGroup,
    cancelAnimationObjectLinkDrag,
  } = pointerRouter;
  clearPointerHoverRef.current = clearPointerHover;

  useEffect(() => {
    const handleAnimationObjectLinkCancel = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || event.metaKey || event.ctrlKey || event.altKey) {
        return;
      }
      if (!cancelAnimationObjectLinkDrag()) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
    };

    window.addEventListener("keydown", handleAnimationObjectLinkCancel, true);
    return () => window.removeEventListener("keydown", handleAnimationObjectLinkCancel, true);
  });

  function handleContextMenu(event: React.MouseEvent<HTMLCanvasElement>): void {
    if (!canvasRef.current) {
      return;
    }

    event.preventDefault();

    const frameLabelHit = hitTestFrameLabel(
      frameLabelOverlays(document, cameraRef.current, previews, resolvedActivePageId, frameLabelViewportBounds(), { allowScripts }),
      event.clientX,
      event.clientY,
    );
    if (frameLabelHit) {
      onSelectionChange([frameLabelHit.id], frameLabelHit.id);
      onContextMenu(frameLabelHit.id, { x: event.clientX, y: event.clientY });
      return;
    }

    const point = screenToDocument(event.clientX, event.clientY, canvasRef.current, cameraRef.current);
    const hit = hitTestDocument(document, point, selectionBounds, cameraRef.current.zoom, hitTestOptions({
      deepSelect: deepSelectModifier(event),
      selectedNodeIds: editableSelectedNodeIds,
    }));
    const frameHit = hit.type === "empty" ? findTopFrameAtPoint(document, point, resolvedActivePageId) : undefined;
    const nodeId = hit.type === "node" ? hit.nodeId : frameHit?.id;
    if (!nodeId) {
      return;
    }

    // Keep existing multi-selection when right-clicking an already-selected node.
    if (!editableSelectedNodeIds.includes(nodeId)) {
      onSelectionChange([nodeId], nodeId);
    }
    onContextMenu(nodeId, { x: event.clientX, y: event.clientY });
  }

  function handleFrameLabelDoubleClick(event: React.MouseEvent<Element>, frameId: string): void {
    event.preventDefault();
    event.stopPropagation();
    onSelectionChange([frameId], frameId);
    setRenamingFrameId(frameId);
    scheduleOverlayRender();
  }

  function commitFrameRename(frameId: string, value: string): void {
    setRenamingFrameId(undefined);
    scheduleOverlayRender();
    onRenameNode(frameId, value);
  }

  function handleFrameLabelWheel(event: React.WheelEvent<Element>): void {
    event.preventDefault();
    event.stopPropagation();
    handleWheel(event.nativeEvent);
  }

  function handleDoubleClick(event: React.MouseEvent<HTMLCanvasElement>): void {
    const frameLabelHit = hitTestFrameLabel(
      frameLabelOverlays(document, cameraRef.current, previews, resolvedActivePageId, frameLabelViewportBounds(), { allowScripts }),
      event.clientX,
      event.clientY,
    );
    if (frameLabelHit) {
      if (hitTestFrameLabelScriptAction(frameLabelHit, event.clientX, event.clientY)) {
        return;
      }
      handleFrameLabelDoubleClick(event, frameLabelHit.id);
      return;
    }

    if (canvasRef.current && activeTool === "select") {
      const point = screenToDocument(event.clientX, event.clientY, canvasRef.current, cameraRef.current);
      const hitTextNode = (() => {
        if (selectedTextNode && pointInBounds(point, selectedTextNode.bounds)) {
          return selectedTextNode;
        }

        const hit = hitTestDocument(document, point, selectionBounds, cameraRef.current.zoom, hitTestOptions({
          deepSelect: deepSelectModifier(event),
          selectedNodeIds: editableSelectedNodeIds,
        }));
        if (hit.type !== "node") {
          return undefined;
        }

        const node = getAllSceneNodes(document).find(candidate => candidate.id === hit.nodeId);
        return node?.type === "text" && !isNodeLockedForEditing(document, node.id) ? node : undefined;
      })();

      if (hitTextNode) {
        const index = textIndexAtPoint(hitTextNode, point, Math.round(cameraRef.current.zoom * 10));
        const { start, end } = wordBoundariesAtIndex(hitTextNode.text.characters, index);
        const nextSelection = { anchor: start, focus: end };

        if (mode === "textEdit" && selectedTextNode?.id === hitTextNode.id) {
          updateTextSelection(nextSelection);
          setTextareaSelection(textInputRef.current, start, end);
          textInputRef.current?.focus();
          return;
        }

        pendingTextSelectionRef.current = nextSelection;
        if (primarySelectedNodeId !== hitTextNode.id) {
          onSelectionChange([hitTextNode.id], hitTextNode.id);
        }
        onEnterTextMode(hitTextNode.id);
        return;
      }
    }

    if (canvasRef.current && mode === "select" && activeTool === "select") {
      const baseContainerId = doubleClickBaseContainerRef.current ?? primarySelectedNodeId;
      const primaryNode = getAllSceneNodes(document).find(node => node.id === baseContainerId);
      if (primaryNode && isDoubleClickContainer(primaryNode)) {
        const point = screenToDocument(event.clientX, event.clientY, canvasRef.current, cameraRef.current);
        const childId = hitTestDirectChildOfContainer(document, primaryNode.id, point, cameraRef.current.zoom);
        if (childId) {
          onSelectionChange([childId], childId);
          return;
        }
      }
    }

    if (selectedTextNode) {
      onEnterTextMode(selectedTextNode.id);
      return;
    }

    if (primarySelectedNodeId) {
      onEnterVectorMode();
    }
  }

  function handleWheel(event: WheelEvent): void {
    event.preventDefault();
    if (!canvasRef.current) {
      return;
    }

    const deltaX = normalizeWheelDelta(event.deltaX, event.deltaMode);
    const deltaY = normalizeWheelDelta(event.deltaY, event.deltaMode);
    const currentCamera = cameraRef.current;

    if (event.ctrlKey && safariPinchRef.current.active) {
      return;
    }

    if (!event.ctrlKey) {
      updateCamera({
        ...currentCamera,
        x: currentCamera.x + (deltaX * WHEEL_PAN_SPEED) / currentCamera.zoom,
        y: currentCamera.y + (deltaY * WHEEL_PAN_SPEED) / currentCamera.zoom,
      });
      return;
    }

    const canvasRect = canvasRef.current.getBoundingClientRect();
    const before = screenToDocument(event.clientX, event.clientY, canvasRef.current, currentCamera);
    const nextZoom = clampZoom(currentCamera.zoom * Math.exp(-deltaY * 0.01 * WHEEL_ZOOM_SPEED));
    updateCamera({
      ...currentCamera,
      zoom: nextZoom,
      x: before.x - (event.clientX - canvasRect.left) / nextZoom,
      y: before.y - (event.clientY - canvasRect.top) / nextZoom,
    }, { zoomGesture: true });
  }

  function beginGuideCreateFromRuler(orientation: "horizontal" | "vertical", event: React.PointerEvent<HTMLDivElement>): void {
    event.preventDefault();
    event.stopPropagation();
    workspaceRef.current?.setPointerCapture(event.pointerId);
    const point = canvasRef.current
      ? screenToDocument(event.clientX, event.clientY, canvasRef.current, cameraRef.current)
      : { x: 0, y: 0 };
    dragRef.current = {
      mode: "guideCreate",
      startClientX: event.clientX,
      startClientY: event.clientY,
      startDocumentX: point.x,
      startDocumentY: point.y,
      startBoundsById: new Map(),
      nodeIds: [],
      guideOrientation: orientation,
      moved: false,
    };
    onGuideSelectionChange(undefined);
    onSelectionChange([], undefined);
  }

  function handleImageFileDragOver(event: React.DragEvent<HTMLElement>): void {
    if (animationModeActive && event.dataTransfer.types.includes(CURVA_SCENE_NODE_DRAG_TYPE)) {
      event.preventDefault();
      event.dataTransfer.dropEffect = "copy";
      if (!canvasRef.current) {
        return;
      }
      const point = screenToDocument(event.clientX, event.clientY, canvasRef.current, cameraRef.current);
      const endpoint = nearestAnimationObjectInputPin(animationGraph.nodes, point);
      // Drag data is in protected mode during dragover in browsers, so use the
      // in-process session for live feedback and parse DataTransfer on drop.
      const payload = getActiveSceneNodeDrag();
      const source = payload ? findSceneNode(document, payload.sourceNodeId) : undefined;
      const draggedCount = payload?.nodeIds.length ?? selectedNodeIds.length;
      setPrototypeLinkPreview(source ? { from: rectCenter(source.bounds), to: point } : undefined);
      setAnimObjectDragGhost(endpoint
        ? undefined
        : {
            point,
            kind: draggedCount > 1 ? "group" : source?.type === "frame" ? "animation" : "animate",
          });
      scheduleOverlayRender();
      return;
    }

    if (animationModeActive && event.dataTransfer.types.includes("application/x-curva-animation-node")) {
      event.preventDefault();
      event.dataTransfer.dropEffect = "copy";
      return;
    }

    if (
      allowScripts
      && onScriptEntryDrop
      && mode !== "textEdit"
      && event.dataTransfer.types.includes(CURVA_SCRIPT_ENTRY_DRAG_TYPE)
      && canvasRef.current
    ) {
      event.preventDefault();
      const drag = getActiveScriptEntryDrag();
      if (drag?.kind === "folder") {
        event.dataTransfer.dropEffect = "none";
        return;
      }
      const point = screenToDocument(event.clientX, event.clientY, canvasRef.current, cameraRef.current);
      const frameId = topFrameIdAtPoint(document, point, resolvedActivePageId);
      event.dataTransfer.dropEffect = frameId ? "copy" : "none";
      return;
    }

    if (mode === "textEdit" || !onImageFileDrop || !isImageFileDrag(event.dataTransfer)) {
      return;
    }

    event.preventDefault();
    event.dataTransfer.dropEffect = "copy";
  }

  function handleImageFileDrop(event: React.DragEvent<HTMLElement>): void {
    const sceneNodeDrag = parseSceneNodeDrag(event.dataTransfer.getData(CURVA_SCENE_NODE_DRAG_TYPE))
      ?? getActiveSceneNodeDrag();
    if (animationModeActive && sceneNodeDrag && canvasRef.current) {
      event.preventDefault();
      event.stopPropagation();
      const nodeIds = sceneNodeDrag.nodeIds.filter(nodeId => Boolean(findSceneNode(document, nodeId)));
      const sourceNodeId = nodeIds.includes(sceneNodeDrag.sourceNodeId) ? sceneNodeDrag.sourceNodeId : nodeIds[0];
      const source = sourceNodeId ? findSceneNode(document, sourceNodeId) : undefined;
      const point = screenToDocument(event.clientX, event.clientY, canvasRef.current, cameraRef.current);
      const endpoint = nearestAnimationObjectInputPin(animationGraph.nodes, point);
      setActiveSceneNodeDrag(undefined);
      setPrototypeLinkPreview(undefined);
      setAnimObjectDragGhost(undefined);

      const action = animationSceneNodeDropAction(nodeIds, sourceNodeId, source?.type, endpoint);
      if (action?.kind === "connectTargets") {
        onAnimationTargetsConnect?.(action.sourceNodeIds.map(nodeId => ({ kind: "sceneNode" as const, nodeId })), action.endpoint);
      } else if (action?.kind === "connectObject") {
        onAnimationObjectConnect?.(action.sourceNodeId, action.endpoint);
      } else if (action?.kind === "createGroup") {
        onAnimationObjectsCreateGroupNode?.(action.sourceNodeIds, point);
      } else if (action?.kind === "createNode") {
        onAnimationObjectCreateNode?.(action.sourceNodeId, point);
      }
      scheduleOverlayRender();
      return;
    }

    const animationNodeKind = event.dataTransfer.getData("application/x-curva-animation-node") as AnimationNodeKind;
    if (animationModeActive && animationNodeKind && onAnimationNodeInsert && canvasRef.current) {
      event.preventDefault();
      const point = screenToDocument(event.clientX, event.clientY, canvasRef.current, cameraRef.current);
      const frameId = animationNodeKind === "animation" ? topFrameIdAtPoint(document, point, resolvedActivePageId) : undefined;
      onAnimationNodeInsert(createAnimationGraphNode(animationNodeKind, point, { frameId }));
      return;
    }

    const scriptEntryId = event.dataTransfer.getData(CURVA_SCRIPT_ENTRY_DRAG_TYPE)
      || getActiveScriptEntryDrag()?.entryId;
    if (scriptEntryId && onScriptEntryDrop && canvasRef.current) {
      event.preventDefault();
      event.stopPropagation();
      if (!allowScripts) {
        uiStore.setMessage("Scripts are disabled. Enable Allow Scripts in the View menu.");
        return;
      }
      const drag = getActiveScriptEntryDrag();
      const source = drag?.source ?? "document";
      const entry = source === "editor"
        ? findScriptEntry(editorScriptsStore.getLibrary(), scriptEntryId)
        : findScriptEntry(document.scripts, scriptEntryId);
      if (!entry || entry.type !== "script") {
        return;
      }
      const point = screenToDocument(event.clientX, event.clientY, canvasRef.current, cameraRef.current);
      const frameId = topFrameIdAtPoint(document, point, resolvedActivePageId);
      if (!frameId) {
        uiStore.setMessage("Drop on a frame to run this script.");
        return;
      }
      onScriptEntryDrop(entry.id, frameId, source);
      return;
    }

    if (mode === "textEdit" || !onImageFileDrop || !canvasRef.current || !isImageFileDrag(event.dataTransfer)) {
      return;
    }

    event.preventDefault();
    const point = screenToDocument(event.clientX, event.clientY, canvasRef.current, cameraRef.current);
    onImageFileDrop(event.dataTransfer, point);
  }

  function handleCanvasDragLeave(event: React.DragEvent<HTMLElement>): void {
    const related = event.relatedTarget;
    if (related instanceof Node && event.currentTarget.contains(related)) {
      return;
    }
    setPrototypeLinkPreview(undefined);
    setAnimObjectDragGhost(undefined);
    scheduleOverlayRender();
  }

  return (
    <main
      ref={workspaceRef}
      className="relative h-full w-full min-w-0 overflow-hidden bg-[#111418]"
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerCancel}
      onDragOver={handleImageFileDragOver}
      onDragLeave={handleCanvasDragLeave}
      onDrop={handleImageFileDrop}
    >
      <div className="absolute inset-0 bg-[linear-gradient(#ffffff0a_1px,transparent_1px),linear-gradient(90deg,#ffffff0a_1px,transparent_1px)] bg-[size:24px_24px]" />
      <canvas
        ref={canvasRef}
        className={`absolute inset-0 h-full w-full touch-none ${showTileGaps ? "invisible" : ""}`}
        onDoubleClick={handleDoubleClick}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerCancel}
        onPointerLeave={handlePointerLeave}
        onContextMenu={handleContextMenu}
      />
      <canvas
        ref={tileGapsCanvasRef}
        className={`pointer-events-none absolute inset-0 z-[6] h-full w-full touch-none ${showTileGaps ? "block" : "hidden"}`}
      />
      <canvas
        ref={animationCanvasRef}
        className="pointer-events-none absolute left-0 top-0 z-[7] origin-top-left will-change-transform"
        style={animationFrameOverlay ? {
          width: animationFrameOverlay.bounds.width * cameraRef.current.zoom,
          height: animationFrameOverlay.bounds.height * cameraRef.current.zoom,
          transform: `translate(${(animationFrameOverlay.bounds.x - cameraRef.current.x) * cameraRef.current.zoom}px, ${(animationFrameOverlay.bounds.y - cameraRef.current.y) * cameraRef.current.zoom}px)`,
          display: "block",
        } : { display: "none" }}
      />
      <AnimationGraphOverlay
        ref={animationGraphOverlayRef}
        graph={animationGraph ?? createEmptyAnimationGraph()}
        document={document}
        camera={cameraRef.current}
        active={animationModeActive}
        selectedNodeIds={selectedAnimationNodeIds}
        selectedConnectionIds={selectedAnimationConnectionIds}
        selectedObjectConnectionKeys={selectedObjectConnectionKeys}
        frames={animationFrames}
        objectDragGhost={armedAnimationNodeGhost ?? animObjectDragGhost}
        playing={animationPlaying}
        onInsertNode={node => onAnimationNodeInsert?.(node)}
        onSelectNodes={(nodeIds, primaryNodeId) => onAnimationNodeSelectionChange?.(nodeIds, primaryNodeId)}
        onSelectConnections={connectionIds => onAnimationConnectionSelectionChange?.(connectionIds)}
        onSelectObjectConnections={keys => onAnimationObjectConnectionSelectionChange?.(keys)}
        onMoveNodes={(before, after) => onAnimationNodesMove?.(before, after)}
        onDuplicateNodes={after => onAnimationNodesDuplicate?.(after)}
        onConnect={connection => onAnimationConnect?.(connection)}
        onObjectConnect={(sourceNodeId, endpoint) => onAnimationObjectConnect?.(sourceNodeId, endpoint)}
        onInsertAndConnect={(node, connection) => onAnimationInsertAndConnect?.(node, connection)}
        onUpdateNode={(node, label) => onAnimationNodeUpdate?.(node, label)}
        onUpdateVisualGroup={(group, label) => onAnimationVisualGroupUpdate?.(group, label)}
        onDeleteVisualGroup={groupId => onAnimationVisualGroupDelete?.(groupId)}
        onAddAnimateProperty={nodeId => onAnimationAddAnimateProperty?.(nodeId)}
        onPlay={rootNodeId => onAnimationPlay?.(rootNodeId)}
        onPause={() => onAnimationPause?.()}
        visibleViewportInsets={visibleViewportInsets ?? { top: 0, left: 0, right: 0, bottom: 0 }}
        onAutopanScreenDelta={panCameraByScreenDelta}
        onWheelGesture={event => {
          event.stopPropagation();
          handleWheel(event.nativeEvent);
        }}
      />
      {showRulers ? (
        <RulerOverlay
          camera={cameraRef.current}
          visibleViewportInsets={visibleViewportInsets ?? { top: 0, left: 0, right: 0, bottom: 0 }}
          canvasWidth={canvasSize.width}
          canvasHeight={canvasSize.height}
          pointerCanvasOffset={pointerCanvasOffset}
          pointerDocumentPosition={pointerDocumentPosition}
          pointerInsideCanvas={pointerInsideCanvas}
          onHorizontalRulerPointerDown={event => beginGuideCreateFromRuler("horizontal", event)}
          onVerticalRulerPointerDown={event => beginGuideCreateFromRuler("vertical", event)}
        />
      ) : null}
      {colorSampler && colorSamplePreview ? (
        <ColorSampleLoupe
          preview={colorSamplePreview}
          mode={colorSampler.mode}
        />
      ) : null}
      <div className="pointer-events-none absolute inset-0">
        {renamingFrameId ? frameLabelOverlays(document, cameraRef.current, previews, resolvedActivePageId, frameLabelViewportBounds(cameraRef.current), { allowScripts }).filter(frame => renamingFrameId === frame.id).map(frame => (
          <FrameNameInput
            key={frame.id}
            frame={frame}
            onCommit={value => commitFrameRename(frame.id, value)}
            onCancel={() => setRenamingFrameId(undefined)}
            onWheel={handleFrameLabelWheel}
          />
        )) : null}
      </div>
      <textarea
        ref={textInputRef}
        tabIndex={mode === "textEdit" ? 0 : -1}
        defaultValue={selectedTextNode?.text.characters ?? ""}
        onChange={event => {
          if (selectedTextNode) {
            recordTextUndoSnapshot(textInputCommittedValueRef.current, textSelectionRef.current);
            textRedoStackRef.current = [];
            onTextCharactersChange(selectedTextNode.id, event.currentTarget.value);
            textInputCommittedValueRef.current = event.currentTarget.value;
            setCaretVisible(true);
            updateTextSelection(selectionFromTextarea(event.currentTarget));
          }
        }}
        onSelect={event => {
          setCaretVisible(true);
          updateTextSelection(selectionFromTextarea(event.currentTarget));
        }}
        onFocus={() => updateTextInputFocus(mode === "textEdit")}
        onBlur={() => updateTextInputFocus(false)}
        onKeyDown={event => {
          setCaretVisible(true);
          if (event.key === "Escape" && !event.metaKey && !event.ctrlKey && !event.altKey) {
            event.preventDefault();
            onTextEditExit();
            return;
          }

          if (selectedTextNode && isModKey(event) && event.key.toLowerCase() === "z") {
            if (event.shiftKey) {
              if (textRedoStackRef.current.length > 0) {
                event.preventDefault();
                redoTextEdit(selectedTextNode.id, event.currentTarget);
              } else {
                event.preventDefault();
                onRedo();
              }
            } else {
              if (textUndoStackRef.current.length > 0) {
                event.preventDefault();
                undoTextEdit(selectedTextNode.id, event.currentTarget);
              } else {
                event.preventDefault();
                onUndo();
              }
            }
            return;
          }

          if (isModKey(event) && isArrowKey(event.key)) {
            event.preventDefault();
            const next = commandArrowSelection(event.currentTarget, event.key, event.shiftKey);
            setTextareaSelection(event.currentTarget, next.anchor, next.focus);
            updateTextSelection(next);
            return;
          }

          if (selectedTextNode && (event.key === "Backspace" || event.key === "Delete") && hasTextRange(textSelectionRef.current)) {
            event.preventDefault();
            const range = normalizedTextRange(textSelectionRef.current);
            recordTextUndoSnapshot(event.currentTarget.value, textSelectionRef.current);
            textRedoStackRef.current = [];
            const next = textWithDeletedSelection(event.currentTarget.value, range.start, range.end);
            event.currentTarget.value = next.value;
            setTextareaSelection(event.currentTarget, next.cursor, next.cursor);
            onTextCharactersChange(selectedTextNode.id, next.value);
            textInputCommittedValueRef.current = next.value;
            updateTextSelection({ anchor: next.cursor, focus: next.cursor });
            return;
          }

          if (selectedTextNode && isModKey(event) && event.key.toLowerCase() === "b") {
            event.preventDefault();
            const range = normalizedTextRange(textSelectionRef.current);
            onTextBoldShortcut(selectedTextNode.id, range.start, range.end);
          }
        }}
        className="absolute left-0 top-0 h-px w-px resize-none opacity-0 outline-none"
        aria-label="Text editor input"
      />
    </main>
  );

  function recordTextUndoSnapshot(value: string, selection: { anchor: number; focus: number }): void {
    const stack = textUndoStackRef.current;
    const previous = stack.at(-1);
    if (previous?.value === value && previous.selection.anchor === selection.anchor && previous.selection.focus === selection.focus) {
      return;
    }

    stack.push({ value, selection });
    if (stack.length > 200) {
      stack.shift();
    }
  }

  function undoTextEdit(nodeId: string, input: HTMLTextAreaElement): void {
    const snapshot = textUndoStackRef.current.pop();
    if (!snapshot) {
      return;
    }

    textRedoStackRef.current.push({ value: input.value, selection: selectionFromTextarea(input) });
    applyTextInputSnapshot(nodeId, input, snapshot);
  }

  function redoTextEdit(nodeId: string, input: HTMLTextAreaElement): void {
    const snapshot = textRedoStackRef.current.pop();
    if (!snapshot) {
      return;
    }

    textUndoStackRef.current.push({ value: input.value, selection: selectionFromTextarea(input) });
    applyTextInputSnapshot(nodeId, input, snapshot);
  }

  function applyTextInputSnapshot(
    nodeId: string,
    input: HTMLTextAreaElement,
    snapshot: { value: string; selection: { anchor: number; focus: number } },
  ): void {
    input.value = snapshot.value;
    textInputCommittedValueRef.current = snapshot.value;
    const selection = {
      anchor: Math.min(snapshot.selection.anchor, snapshot.value.length),
      focus: Math.min(snapshot.selection.focus, snapshot.value.length),
    };
    setTextareaSelection(input, selection.anchor, selection.focus);
    onTextCharactersChange(nodeId, snapshot.value);
    updateTextSelection(selection);
  }
}

function createDraftPencilNode(
  document: CurvaDocument,
  network: VectorNetwork | undefined,
  points: Array<{ x: number; y: number }>,
  activePageId?: string,
): NonNullable<SceneOverlay["draftVectorNetwork"]> | undefined {
  if (!network) {
    return undefined;
  }

  const frameId = topFrameIdAtPoint(document, points[0] ?? { x: 0, y: 0 }, activePageId);
  const frame = frameId ? getAllSceneNodes(document).find((node): node is FrameNode => node.id === frameId && node.type === "frame") : undefined;
  const node: VectorNetworkNode = {
    ...createPencilVectorNetworkNode("Pencil Preview", network),
    id: "__draft_pencil__",
  };

  return {
    node,
    clip: frame?.clipsContent ? frame.bounds : undefined,
  };
}

/** Safari/WebKit trackpad pinch — not in the standard DOM lib. */
type SafariGestureEvent = Event & {
  scale: number;
  clientX: number;
  clientY: number;
};

